import React from 'react';
import {
  get,
  reject,
  isEmpty,
  groupBy,
  findIndex,
  find,
  has,
  set,
  omit,
  pick,
  forEach,
  cloneDeep,
  some,
  xor,
  isUndefined,
  startsWith,
  flow,
  uniqWith,
  isEqual,
  intersection,
} from 'lodash';
import arrayMove from 'array-move/index';
import { v1 as uuid } from 'uuid';

import { transformPropsDeep } from 'containers/App/utils';

import { FEATURE_ANSWERS, FEATURE_MATRIX } from 'data/questionFeatures';
import { deriveQuotaMode, QUOTA_MODE } from 'data/quotaModes';
import {
  QUESTION_SINGLE,
  QUESTION_MULTIPLE,
  QUESTION_OPEN_ENDED,
  QUESTION_NUMERIC_OPEN_ENDED,
  QUESTION_DESCRIPTION,
  QUESTION_NPS,
  QUESTION_VAN_WESTENDORP,
  QUESTION_RATING,
  QUESTION_RANKING,
  QUESTION_SLIDER,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_MATRIX_BIPOLAR,
  QUESTION_MATRIX_NUMERIC_OPEN_ENDED,
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_CONSTANT_SUM,
  GROUP_BASIC,
  GROUP_AB_TEST,
  GROUP_CONJOINT,
  GROUP_VAN_WESTENDORP,
  GROUP_MAXDIFF,
  // QUESTION_MAXDIFF,
  QUESTION_DRILLDOWN,
  QUESTION_CONVERSATIONAL,
  GROUP_LOOP_MERGE,
  NODE_TYPES_SUPPORT_SIMPLE_QUOTAS,
  GROUP_TYPES_ALLOW_SIMPLE_QUOTAS,
  GROUP_TYPES_ALLOW_ADVANCED_QUOTAS,
  NODE_TYPES_SUPPORT_QUOTA_ASSIGNMENT,
} from 'data/nodeTypes';

import * as PROPERTIES from 'data/nodeProperties';

import {
  generateQuestion,
  canConvertQuestion,
  defaultPristineValues,
  getBackendSupportedQuestionProperties,
  getPreSelectedAttributes,
  questionSupportsFeature,
  isNodeSupportAnswersWithMentions,
  hasPipingOnAnswerItem,
} from 'utils/questionHelpers';

import {
  isBipolarQuestionType,
  isMatrixNumericQuestionType,
  isMatrixConstantSumType,
  isMatrixOpenEndedQuestionType,
  isConversationalType,
  isGroup,
  isAbGroup,
  isBasicGroup,
  isVanWestendorpGroup,
  isConjointOrMaxDiffGroup,
  isLoopAndMergeGroup,
  isOpenEndedQuestionType,
  isSliderQuestionType,
  isDrillDownQuestionType,
  isMatrixSingleMultipleQuestionType,
  isMaxDiffGroup,
  isAbOrVanWestendorpGroup,
  isAbOrVanWestendorpOrConjointOrMaxDiffGroup,
  isMaxDiffGroupNode,
  isConjointGroupNode,
  isAbGroupNode,
  isConversationalNode,
} from 'utils/nodeTypes';
import {
  itemHasCarryForwardFromPreviousRevision,
  itemIsUsedInCarryForwardFromPreviousRevision,
} from 'containers/Permissions/helpers';

import {
  getReferencedSources,
  replaceMentionedPromptsWithBlank,
  replaceRecallTagsInHtmlWithText,
  canQuestionBeMentioned,
  hasPiping as hasPipingFn,
  replaceVariableTagsInHtmlWithText,
  variableRegex,
  pipingRegExAllWithoutVariables,
} from 'components/FormattedInput/helpers';
import {
  getAnswersRemoved,
  getFilteredQuotaStatementsByType,
  updateState,
} from 'containers/QuestionnaireLogic/utils';

import { DOE_MAX_MANUAL_VALUE_WHEN_MAX_LEVELS } from 'containers/QuestionnairePage/NodeSettings/settings/DoeSettings/constants';

import { getToolTip } from 'components/NodeHeaderSettings/utils';
import {
  MENTION_TYPES,
  PIPE_EXPRESSION_MATCHING,
} from 'components/FormattedInput/constants';

import {
  Questionnaire,
  QuestionnaireConditionContext,
  VirtualEntryGroupPostfix,
  ActionVisibilityValueType,
  NodeStatements,
  QuotaConditionTypeEnum,
  questionnaireLogicService,
  ruleService,
} from 'utils/adl-services';
import {
  isSingleAssignmentStrategy,
  resolveQuotaAssignmentStrategyForPersist,
} from 'utils/quotaAssignmentStrategy';
import Pficon from 'components/Pficon';

import { SHUFFLE_NONE } from 'components/DropdownShuffle/constants';
import { hasOnlyDefaultTicks } from './GenericQuestion/SliderQuestion/utils';
import {
  hasOnlyDefaultScorepoints,
  filterVisibleColumns,
} from './GenericQuestion/MatrixQuestion/utils';
import {
  getLevelsWithAnswerRefId,
  getOptionsArrayFromLevels,
} from './GenericQuestion/DrillDownQuestion/utils';

import {
  getAttributeValuesLength,
  isLoopAndMergeAttributeBasedOnQuestion,
} from './QuestionGroup/utils';

import { getMaxAttributeValuesLimit } from './QuestionGroup/Conjoint/utils';

import SingleMultipleValidator, {
  validateQuotas as singleMultipleQuotaValidator,
  validateScreeningAnswers as singleMultipleScreeningValidator,
} from './GenericQuestion/SingleMultipleQuestion/validator';
import PromptOnlyValidator from './GenericQuestion/PromptOnlyQuestion/validator';
import SliderValidator from './GenericQuestion/SliderQuestion/validator';
import MatrixValidator, {
  validateQuotas as matrixQuotaValidator,
} from './GenericQuestion/MatrixQuestion/validator';
import BipolarMatrixValidator, {
  validateQuotas as bipolarQuotaValidator,
} from './GenericQuestion/BipolarQuestion/validator';
import VanWestendorpValidator from './GenericQuestion/VanWestendorpQuestion/validator';
import MatrixOpenEndedQuestionValidator from './GenericQuestion/MatrixOpenEndedQuestion/validator';
import ConversationalValidator from './GenericQuestion/ConversationalQuestion/validator';
import DrillDownValidator from './GenericQuestion/DrillDownQuestion/validator';
import GroupValidator from './QuestionGroup/validator';

import {
  updateRelevantCarriedQuestions,
  removeAllCarryForward,
  clearRelevantCarriedQuestions,
  isQuestionCarryForwarded,
  isQuestionUsedInLoopAndMerge,
  updateRelevantLoopAndMergeGroups,
  clearRelevantLoopAndMergeGroups,
  getMaxLoopAndMergeRepetitions,
} from './NodeSettings/settings/utils/CarryForward';

import {
  QUESTIONNAIRE_SUBSTATE_PATHS,
  RENAME_SLIDER_QUESTION_PROPERTIES,
  ANSWER_NONE_OF_THE_ABOVE,
  ANSWER_DEFAULT,
  nodePropertiesWithPrompt,
  answerPromptProperties,
  nodePropertiesForMentions,
  translationPropertiesForMentions,
  MAX_PIPED_QUESTION_LENGTH,
  OPTION_TYPES,
} from './constants';

import { makeSelectQuestionQuotasMinimalMap } from './selectorUtils/quotasMinimal';
import { getDisplayLabelWithGroupPrefix } from './selectorUtils';
import { getCarriedForwardDataForMatrixAnswer } from './NodeSettings/settings/utils/CarryForward/matrix';
import { MAX_CONVERSATIONAL_AI_QUESTIONS } from './GenericQuestion/ConversationalQuestion/constants';
import { stripHTMLTags } from '../TemplatePreviewPage/QuestionsListViewOnly/GenericQuestionViewOnly/utils';

import messages from './messages';
import { removeNodeQuotaRulesActionHandler } from './reducer';
import {
  decodeScreenerTypes,
  getNodeStatements,
  makeScreeningQuotaEligibility,
} from './screening';

export const getTangentQuestions = (questionId, questions = []) => {
  const currentQuestionIndex = questions.findIndex(
    ({ refId }) => refId === questionId,
  );
  return {
    previousQuestion: get(
      questions,
      `[${currentQuestionIndex - 1}].refId`,
      null,
    ),
    nextQuestion: get(questions, `[${currentQuestionIndex + 1}].refId`, null),
  };
};

export const getRevalidatedQuestionsAndGlobalErrors = ({
  globalErrors = [],
  questions,
  previousNodesPerNode,
  answersWithLogicFromOutcomes,
  answersWithLogicFromOutcomesHide,
  nodesWithLogicFromOutcomesShow,
  hiddenNodes,
  screeningQuestions,
  filter = defaultIdentityTrue,
  surveyConfig,
}) => {
  let questionnaireErrors = globalErrors;
  const updatedQuestions = questions.map((question) => {
    if (filter(question)) {
      const nodeErrors = getNodeErrors({
        node: question,
        nodes: questions,
        screeningQuestions,
        previousNodesPerNode,
        answersWithLogicFromOutcomes,
        answersWithLogicFromOutcomesHide,
        nodesWithLogicFromOutcomesShow,
        hiddenNodes,
        surveyConfig,
      });
      questionnaireErrors = updateQuestionnaireErrors({
        globalErrors: questionnaireErrors,
        nodeRefId: question.refId,
        groupRefId: question.groupRefId,
        nodeErrors,
        nodeType: question.type,
      });
      return { ...question, errors: nodeErrors };
    }
    return question;
  });
  return { questions: updatedQuestions, questionnaireErrors };
};

export const getNodeErrors = ({
  node,
  nodes = [],
  screeningQuestions = [],
  previousNodesPerNode = {},
  answersWithLogicFromOutcomes = [],
  answersWithLogicFromOutcomesHide = [],
  nodesWithLogicFromOutcomesShow = [],
  hiddenNodes = [],
  groupNode,
  surveyConfig,
}) => {
  const allNodes = [...screeningQuestions, ...nodes].map((item) => {
    if (
      nodesWithLogicFromOutcomesShow.includes(item.refId) ||
      hiddenNodes.includes(item.refId)
    ) {
      return {
        ...item,
        hasNodeDisplayLogic: true,
      };
    }

    return item;
  });

  switch (node.type) {
    case QUESTION_SINGLE:
    case QUESTION_MULTIPLE:
    case QUESTION_RANKING:
      // case QUESTION_MAXDIFF:
      return SingleMultipleValidator(
        node,
        nodes,
        previousNodesPerNode,
        answersWithLogicFromOutcomes,
        answersWithLogicFromOutcomesHide,
        screeningQuestions,
        groupNode,
        allNodes,
        surveyConfig,
      );
    case QUESTION_VAN_WESTENDORP:
      return VanWestendorpValidator(node);
    case QUESTION_OPEN_ENDED:
    case QUESTION_NUMERIC_OPEN_ENDED:
    case QUESTION_DESCRIPTION:
    case QUESTION_NPS:
    case QUESTION_RATING:
      return PromptOnlyValidator(node);
    case QUESTION_SLIDER:
      return SliderValidator(node);
    case QUESTION_DRILLDOWN:
      return DrillDownValidator(node);
    case QUESTION_MATRIX_SINGLE:
    case QUESTION_MATRIX_MULTIPLE:
      return MatrixValidator({
        node,
        nodes,
        previousNodesPerNode,
        answersWithLogicFromOutcomes,
        answersWithLogicFromOutcomesHide,
        screeningQuestions,
        allNodes,
        surveyConfig,
      });
    case QUESTION_MATRIX_BIPOLAR:
      return BipolarMatrixValidator({
        node,
        allNodes,
        answersWithLogicFromOutcomes,
        answersWithLogicFromOutcomesHide,
        surveyConfig,
      });
    case QUESTION_MATRIX_OPEN_ENDED:
    case QUESTION_MATRIX_NUMERIC_OPEN_ENDED:
    case QUESTION_MATRIX_CONSTANT_SUM:
      return MatrixOpenEndedQuestionValidator({
        node,
        nodes,
        previousNodesPerNode,
        answersWithLogicFromOutcomes,
        answersWithLogicFromOutcomesHide,
        screeningQuestions,
        allNodes,
      });
    case GROUP_BASIC:
    case GROUP_LOOP_MERGE:
    case GROUP_AB_TEST:
    case GROUP_CONJOINT:
    case GROUP_MAXDIFF:
    case GROUP_VAN_WESTENDORP:
      return GroupValidator(
        node,
        nodes,
        previousNodesPerNode,
        screeningQuestions,
      );
    case QUESTION_CONVERSATIONAL:
      return ConversationalValidator(node);
    default:
      return [];
  }
};

export const hasMentionOnRefId = (refId, subRefId) => {
  let regExpString = `{{refId:${refId}(/subRefId:[^}]+)?}}`;
  if (subRefId) {
    regExpString = `{{refId:${refId}/subRefId:${subRefId}}}`;
  }
  const mentionRe = new RegExp(regExpString);

  return ({ prompt, body, hasPiping }) =>
    hasPiping && (mentionRe.test(prompt) || mentionRe.test(body));
};

export const hasAttributeMentionOnRefId = (refId) => {
  const regExpString = `{{attrRefId:${refId}}}`;
  const mentionRe = new RegExp(regExpString);

  return ({ prompt, body, hasPiping }) =>
    hasPiping && (mentionRe.test(prompt) || mentionRe.test(body));
};

const removeRefIdMentions = (refId, subRefId) => {
  let regExpString = `{{refId:${refId}(/subRefId:[^}]+)?}}`;
  if (subRefId) {
    regExpString = `{{refId:${refId}/subRefId:${subRefId}}}`;
  }

  const mentionRe = new RegExp(regExpString, 'g');

  return ({ prompt, htmlPrompt }) => ({
    prompt: prompt.replace(mentionRe, ''),
    htmlPrompt: htmlPrompt.replace(mentionRe, ''),
  });
};

export const removeRefIdMentionFn = (refId, subRefId) => {
  let regExpString = `{{refId:${refId}(/subRefId:[^}]+)?}}`;
  if (subRefId) {
    regExpString = `{{refId:${refId}/subRefId:${subRefId}}}`;
  }

  const mentionRe = new RegExp(regExpString, 'g');

  return (html = '', text = '') => String(html).replace(mentionRe, text);
};

export const removeAttrRefIdMentionsFn = (refId) => {
  const regExpString = `{{attrRefId:${refId}}}`;
  const mentionRe = new RegExp(regExpString, 'g');

  return (html = '', text = '') => String(html).replace(mentionRe, text);
};

export const replaceMentionsRefIdWithOther = ({
  question,
  refId,
  newRefId,
  subRefId,
  newSubRefId,
}) => {
  const { prompt, htmlPrompt } = question;
  const { answers, scorepoints, matrixColumns } = question;
  let updatedAnswers;
  let updatedScorepoints;
  let updatedMatrixColumns;

  if (!isEmpty(answers)) {
    updatedAnswers = answers.map((answer) => {
      const { matrixAnswer } = answer;
      let updatedMatrixAnswer;
      if (matrixAnswer) {
        const { beforeLabel, afterLabel } = matrixAnswer;
        updatedMatrixAnswer = { ...matrixAnswer };
        if (beforeLabel) {
          updatedMatrixAnswer.beforeLabel = replaceMentionsRefIdWithOtherInText(
            beforeLabel,
            refId,
            newRefId,
            subRefId,
            newSubRefId,
          );
        }
        if (afterLabel) {
          updatedMatrixAnswer.afterLabel = replaceMentionsRefIdWithOtherInText(
            afterLabel,
            refId,
            newRefId,
            subRefId,
            newSubRefId,
          );
        }
      }
      const updatedAnswer = {
        ...answer,
        body: replaceMentionsRefIdWithOtherInText(
          answer.body,
          refId,
          newRefId,
          subRefId,
          newSubRefId,
        ),
      };
      if (updatedMatrixAnswer) {
        updatedAnswer.matrixAnswer = updatedMatrixAnswer;
      }
      return updatedAnswer;
    });
  }
  if (!isEmpty(matrixColumns)) {
    updatedMatrixColumns = matrixColumns.map((matrixColumn) => {
      const { beforeLabel, afterLabel } = matrixColumn;
      const updatedMatrixColumn = { ...matrixColumn };
      if (beforeLabel) {
        updatedMatrixColumn.beforeLabel = replaceMentionsRefIdWithOtherInText(
          beforeLabel,
          refId,
          newRefId,
          subRefId,
          newSubRefId,
        );
      }
      if (afterLabel) {
        updatedMatrixColumn.afterLabel = replaceMentionsRefIdWithOtherInText(
          afterLabel,
          refId,
          newRefId,
          subRefId,
          newSubRefId,
        );
      }
      return updatedMatrixColumn;
    });
  }

  if (!isEmpty(scorepoints)) {
    updatedScorepoints = scorepoints.map((scorepoint) => ({
      ...scorepoint,
      body: replaceMentionsRefIdWithOtherInText(
        scorepoint.body,
        refId,
        newRefId,
        subRefId,
        newSubRefId,
      ),
    }));
  }

  return {
    newPrompt: replaceMentionsRefIdWithOtherInText(
      prompt,
      refId,
      newRefId,
      subRefId,
      newSubRefId,
    ),
    newHtmlPrompt: replaceMentionsRefIdWithOtherInText(
      htmlPrompt,
      refId,
      newRefId,
      subRefId,
      newSubRefId,
    ),
    answers: updatedAnswers,
    scorepoints: updatedScorepoints,
    matrixColumns: updatedMatrixColumns,
  };
};

export const replaceMentionsRefIdWithOtherInText = (
  sourceText,
  refId,
  newRefId,
  subRefId,
  newSubRefId,
) => {
  const mentionRefIdRe = new RegExp(`refId:${refId}`, 'g');
  const mentionSubRefIdRe = new RegExp(`subRefId:${subRefId}`, 'g');
  const newRefIdText = `refId:${newRefId}`;
  const newSubRefIdText = `subRefId:${newSubRefId}`;

  let newText = sourceText.replace(mentionRefIdRe, newRefIdText);
  if (subRefId && newSubRefId) {
    newText = newText.replace(mentionSubRefIdRe, newSubRefIdText);
  }

  return newText;
};

export const replaceMentionsAttrRefIdWithOtherInText = (
  sourceText,
  attrRefIds,
  newAttrRefIds,
) => {
  let newText = sourceText;
  attrRefIds.forEach((attrRefId, index) => {
    const mentionRefIdRe = new RegExp(`attrRefId:${attrRefId}`, 'g');
    const newRefIdText = `attrRefId:${newAttrRefIds[index]}`;
    newText = newText.replace(mentionRefIdRe, newRefIdText);
  });

  return newText;
};

export function removeAllMentionsFromAnswer(
  answer,
  replacerFn = replaceRecallTagsInHtmlWithText,
) {
  if (!answer.hasPiping) {
    return answer;
  }

  const updates = answerPromptProperties.reduce((accumulator, property) => {
    if (answer[property]) {
      accumulator[property] = replacerFn(answer[property]);
    }

    return accumulator;
  }, {});
  const hasPiping = Object.keys(updates).some((key) =>
    hasPipingFn(updates[key]),
  );

  return {
    ...answer,
    ...updates,
    hasPiping,
  };
}

function applyUpdatesOnAnswers(node, updateFn, ...restArgs) {
  return nodePropertiesWithPrompt.reduce((accumulator, property) => {
    if (typeof property === 'string' && node[property]) {
      accumulator[property] = node[property].map((item) =>
        updateFn(item, ...restArgs),
      );
    }

    if (typeof property === 'object' && node[property.name]) {
      accumulator[property.name] = {
        ...node[property.name],
      };

      return property.values.reduce((acc, nestedProperty) => {
        if (node[property.name][nestedProperty]) {
          acc[property.name][nestedProperty] = node[property.name][
            nestedProperty
          ].map((item) => updateFn(item, ...restArgs));
        }

        return acc;
      }, accumulator);
    }

    return accumulator;
  }, {});
}

function attachHasPipingOnAnswer(answer) {
  return {
    ...answer,
    hasPiping: hasPipingOnAnswerItem(answer),
  };
}

export function attachHasPipingOnNode(node) {
  if (!isNodeSupportAnswersWithMentions(node)) {
    return {
      ...node,
      hasPiping: hasPipingFn(node.prompt),
    };
  }

  return {
    ...node,
    hasPiping: hasPipingFn(node.prompt),
    ...applyUpdatesOnAnswers(node, attachHasPipingOnAnswer),
  };
}

export function removeAllMentionsFromAnswers(node, replacerFn) {
  if (!isNodeSupportAnswersWithMentions(node)) {
    return {};
  }

  return applyUpdatesOnAnswers(node, removeAllMentionsFromAnswer, replacerFn);
}

export const removeAllMentionsFromNode = (
  node,
  replacerFn = replaceRecallTagsInHtmlWithText,
) => {
  const { htmlPrompt, prompt, hasPiping } = node;
  const newPrompt = replacerFn(prompt);
  const updates = hasPiping
    ? {
        htmlPrompt: replacerFn(htmlPrompt),
        prompt: newPrompt,
        hasPiping: hasPipingFn(newPrompt),
      }
    : {};

  return {
    ...node,
    ...updates,
    ...removeAllMentionsFromAnswers(node, replacerFn),
    forceUpdate: new Date().getTime(),
  };
};

export const removeAllVariableMentionsFromNode = (node) =>
  removeAllMentionsFromNode(node, replaceVariableTagsInHtmlWithText);

export const updateQuestionnaireErrors = ({
  globalErrors,
  nodeRefId,
  groupRefId,
  nodeErrors,
  nodeType,
}) => {
  let newGlobalErrors = reject(globalErrors, ['questionRefId', nodeRefId]);

  if (!groupRefId && !isGroup(nodeType)) {
    newGlobalErrors = reject(newGlobalErrors, ['groupRefId', nodeRefId]);
  }

  if (!isEmpty(nodeErrors)) {
    newGlobalErrors.push({
      questionRefId: nodeRefId,
      groupRefId,
      errors: nodeErrors,
    });
  }
  return newGlobalErrors;
};

export function getCombinedReplaceFnForMentionsOnNode({
  nodeRefId,
  nodesToClearMentions = [],
}) {
  const mentionsOnNode = nodesToClearMentions.filter(
    (item) => item.refId === nodeRefId,
  );
  let combinedMentionRe = new RegExp();

  if (mentionsOnNode.length > 0) {
    const mentionPattern = mentionsOnNode
      .map((item) =>
        item.type === MENTION_TYPES.QUESTION
          ? `{{refId:${item.mentionRefId}(/subRefId:[^}]+)?}}`
          : `{{attrRefId:${item.mentionRefId}}}`,
      )
      .join('|');

    combinedMentionRe = new RegExp(mentionPattern, 'g');
  }

  return {
    mentionsOnNode,
    replaceRecallFn: (text) => text.replace(combinedMentionRe, ''),
  };
}

export function getPromptsWithoutSpecificMentionsForNode({
  node,
  nodesToClearMentions,
}) {
  const { mentionsOnNode, replaceRecallFn } =
    getCombinedReplaceFnForMentionsOnNode({
      nodeRefId: node.refId,
      nodesToClearMentions,
    });

  return mentionsOnNode.length > 0
    ? removeAllMentionsFromNode(node, replaceRecallFn)
    : {};
}

export function getPromptsWithoutSpecificMentions({
  node,
  nodesToClearMentions,
  scrollToRefId,
}) {
  return {
    ...getPromptsWithoutSpecificMentionsForNode({
      node,
      nodesToClearMentions,
    }),
    questions: node.questions
      ? clearMentionsFromNodes({
          scrollToRefId,
          nodes: node.questions,
          nodesToClearMentions,
        })
      : undefined,
  };
}

export function getNodeByRefId(nodes, refId, groupRefId) {
  if (groupRefId) {
    const group = nodes.find((node) => node.refId === groupRefId);

    return group.questions.find((subNode) => subNode.refId === refId);
  }

  return nodes.find((node) => node.refId === refId);
}

// Simple memoization cache keyed by the nodes array reference.
// Assumes nodes are treated immutably (new array when changed).
const flattenAllNodesCache = new Map();

export function flattenAllNodes(nodes) {
  const cached = flattenAllNodesCache.get(nodes);
  if (cached) return cached;
  const flattened = [];

  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];

    if (node.questions) {
      flattened.push(node, ...node.questions);
    } else {
      flattened.push(node);
    }
  }

  flattenAllNodesCache.clear();
  flattenAllNodesCache.set(nodes, flattened);
  return flattened;
}

export function getNodeDeepByCondition(nodes, predicateFn) {
  return flattenAllNodes(nodes).find(predicateFn);
}

export function isSomeNodeDeepByCondition(nodes, predicateFn) {
  return flattenAllNodes(nodes).some(predicateFn);
}

export function getNodeByRefIdDeep(nodes, refId) {
  return getNodeDeepByCondition(nodes, (node) => node.refId === refId);
}

const addNodeOnList = (nodes, refId, newNode, offset = 1) => {
  const newNodes = [...nodes];
  const index = refId ? findIndex(nodes, { refId }) + offset : nodes.length;

  newNodes.splice(index, 0, newNode);

  return newNodes;
};

const addScreeningNodeOnList = (nodes, refId, newNode, offset = 1) => {
  const newNodes = [...nodes];
  const screeningQuestions = newNodes.filter((node) => node.isScreening);
  let index;

  // user pressing the big Add new question button at the bottom
  if (!refId) {
    index = screeningQuestions.length;
  } else {
    const nodeIndex = findIndex(nodes, { refId });

    index = nodeIndex + offset;
  }

  newNodes.splice(index, 0, newNode);

  return newNodes;
};

function attachAttributesOnNode(group, node) {
  if (isAbOrVanWestendorpGroup(group.type)) {
    return {
      ...node,
      selectedAttributes: getPreSelectedAttributes(group.attributes),
    };
  }

  return node;
}

export function getNodesOnAdd(nodes, refId, groupRefId, newNode, offset) {
  if (groupRefId) {
    const group = getNodeByRefId(nodes, groupRefId);
    // node has selected attributes during duplicate of a question
    const updatedNode = isEmpty(newNode.selectedAttributes)
      ? attachAttributesOnNode(group, newNode)
      : newNode;
    const newSubNodes = addNodeOnList(
      group.questions,
      refId,
      updatedNode,
      offset,
    );
    let newNodeGroup;

    return {
      newNodes: nodes.map((node) => {
        if (node.refId === groupRefId) {
          newNodeGroup = {
            ...node,
            questions: newSubNodes,
          };

          return newNodeGroup;
        }

        return node;
      }),
      newNode: newNodeGroup,
    };
  }

  const action = newNode.isScreening ? addScreeningNodeOnList : addNodeOnList;

  return {
    newNodes: action(nodes, refId, newNode, offset),
    newNode,
  };
}

const updateNodeOnList = (nodes, updatedNode, activeNodeRefId) => {
  const previousNode = getNodeByRefId(nodes, updatedNode.refId);

  const canRecallFromNewType = canQuestionBeMentioned({
    fromQuestion: updatedNode,
  });

  const hasChangedNodeType = previousNode.type !== updatedNode.type;

  let removeMentions;
  if (!canRecallFromNewType) {
    removeMentions = removeRefIdMentions(updatedNode.refId);
  } else {
    const removedAnswers = getAnswersRemoved(
      previousNode.answers,
      updatedNode.answers,
      updatedNode.type,
    );
    if (removedAnswers.length) {
      removeMentions = flow(
        removedAnswers.map(({ refId: subRefId }) =>
          removeRefIdMentions(updatedNode.refId, subRefId),
        ),
      );
    }
  }

  let updatedNodes = nodes.map((node) => {
    if (node.refId === updatedNode.refId) {
      // isFavorite is managed exclusively via FavoriteCheckbox → UPDATE_QUESTION_FAVORITE_STATE,
      // never through UPDATE_QUESTIONNAIRE. updatedNode.isFavorite is always stale constructor
      // state from GenericQuestion (undefined for new questions, the original backend value for
      // existing ones) and must never overwrite the current Redux value.
      // quotaLimitType is owned by the quota-mode actions, same as isFavorite:
      // keep the previous node's value, including its absence.
      const { quotaLimitType: incomingMarker, ...updatedNodeWithoutMarker } =
        updatedNode;
      const { isFavorite, quotaLimitType } = previousNode;
      return {
        ...updatedNodeWithoutMarker,
        isFavorite,
        ...(quotaLimitType !== undefined && { quotaLimitType }),
        ignoreScrollIfInViewport: true,
        scrollIntoView:
          activeNodeRefId === updatedNode.refId ? Date.now() : null,
      };
    }

    return removeMentions && node.hasPiping
      ? {
          ...node,
          ...removeMentions(node),
        }
      : node;
  });

  const mustRemoveRelevantSetting =
    hasChangedNodeType &&
    !canConvertQuestion(previousNode.type, updatedNode.type);

  const isCarriedForwardedNode = isQuestionCarryForwarded(
    updatedNode.refId,
    nodes,
  );
  if (isCarriedForwardedNode) {
    updatedNodes = updateRelevantCarriedQuestions({
      questions: updatedNodes,
      sourceQuestion: updatedNode,
      mustRemoveRelevantCarryForwardSetting: mustRemoveRelevantSetting,
    });
  }

  const isUsedInLoopAndMergeNode = isQuestionUsedInLoopAndMerge(
    updatedNode.refId,
    nodes,
  );

  if (isUsedInLoopAndMergeNode) {
    updatedNodes = updateRelevantLoopAndMergeGroups({
      questions: updatedNodes,
      sourceQuestion: updatedNode,
      mustRemoveRelevantLoopAndMergeSetting: mustRemoveRelevantSetting,
    });
  }

  // Universal handling of answer mentions
  // when answers they get removed
  updatedNodes = removeMentionsOfRemovedAnswers(nodes, updatedNodes);
  return { updatedNodes, previousNode };
};

export function removeMentionsOfRemovedAnswers(originalNodes, updatedNodes) {
  const allRemovedAnswers = updatedNodes.flatMap((updatedNode) => {
    const previousNode = originalNodes.find(
      ({ refId }) => refId === updatedNode.refId,
    );
    const removedAnswers = getAnswersRemoved(
      previousNode.answers,
      updatedNode.answers,
      updatedNode.type,
    );

    return removedAnswers.map(({ refId: subRefId }) => ({
      refId: updatedNode.refId,
      subRefId,
    }));
  });

  if (!allRemovedAnswers.length) return updatedNodes;

  const removeMentions = flow(
    allRemovedAnswers.map(({ refId, subRefId }) =>
      removeRefIdMentions(refId, subRefId),
    ),
  );

  return updatedNodes.map((updatedNode) => {
    if (updatedNode.hasPiping) {
      return { ...updatedNode, ...removeMentions(updatedNode) };
    }
    return updatedNode;
  });
}

export function getNodesOnUpdate(nodes, updatedNode, activeNodeRefId) {
  const { groupRefId } = updatedNode;

  if (groupRefId) {
    const groupIndex = findIndex(nodes, { refId: groupRefId });
    const { updatedNodes, previousNode } = updateNodeOnList(
      nodes[groupIndex].questions,
      updatedNode,
      activeNodeRefId,
    );

    return {
      updatedNodes: nodes.map((item) => {
        if (item.refId === groupRefId) {
          return {
            ...item,
            questions: updatedNodes,
          };
        }

        return item;
      }),
      previousNode,
    };
  }

  return updateNodeOnList(nodes, updatedNode, activeNodeRefId);
}

export const moveNodeOnList = (nodes, refId, direction) => {
  const index = findIndex(nodes, ['refId', refId]);
  const newPosition = index + direction;

  return {
    nodeIndex: index,
    newPosition: newPosition >= 0 ? newPosition : 0,
    newNodes: arrayMove(nodes, index, newPosition),
  };
};

export function getNodesOnMove(nodes, refId, groupRefId, direction) {
  if (groupRefId) {
    const group = nodes.find((node) => node.refId === groupRefId);
    const { newNodes, newPosition, nodeIndex } = moveNodeOnList(
      group.questions,
      refId,
      direction,
    );

    return {
      nodeIndex,
      newPosition,
      newNodes: nodes.map((question) => {
        if (question.refId === groupRefId) {
          return {
            ...question,
            questions: newNodes,
          };
        }

        return question;
      }),
    };
  }

  return moveNodeOnList(nodes, refId, direction);
}

export function getNodesOnRemove(nodes, refId, groupRefId) {
  let newNodes = getNodesWithoutMentionsOnRemovedNode(nodes, refId);

  if (groupRefId) {
    const group = newNodes.find((node) => node.refId === groupRefId);
    const newSubNodes = reject(group.questions, ['refId', refId]);

    newNodes = newNodes.map((node) => {
      if (node.refId === groupRefId) {
        return {
          ...node,
          questions: newSubNodes,
        };
      }

      return node;
    });
  } else {
    newNodes = newNodes.filter((node) => node.refId !== refId);

    newNodes = clearRelevantCarriedQuestions({
      questions: newNodes,
      deletedQuestionRefId: refId,
    });

    newNodes = clearRelevantLoopAndMergeGroups({
      questions: newNodes,
      deletedQuestionRefId: refId,
    });
  }

  return newNodes;
}

export function getNodesWithoutMentionsOnRemovedNode(nodes, nodeRefId) {
  const nodeToRemove = nodes.find((node) => node.refId === nodeRefId);
  const nodeRefIds = isGroup(nodeToRemove)
    ? nodeToRemove.questions.map(getEntityRefId)
    : [nodeRefId];

  const mapper = (node) => {
    if (nodeRefIds.includes(node.refId)) {
      return removeAllMentionsFromNode(node);
    }

    if (hasNodeMentions(node)) {
      return removeAllMentionsFromNode(
        node,
        flow(nodeRefIds.map((refId) => removeRefIdMentionFn(refId))),
      );
    }

    return node;
  };

  return nodes.map((node) => {
    if (node.questions) {
      return {
        ...node,
        questions: node.questions.map(mapper),
      };
    }

    return mapper(node);
  });
}

function handleMinMaxLengthSetting(node) {
  if (isMatrixOpenEndedQuestionType(node.type)) {
    return {
      ...node,
      minLength: undefined,
      maxLength: undefined,
      matrix: {
        ...node.matrix,
        validation: {
          ...node.matrix.validation,
          openEndedValidation: {
            ...get(node.matrix, 'validation.openEndedValidation', {
              refId: uuid(),
            }),
            minLength: node.minLength,
            maxLength: node.maxLength,
          },
        },
      },
    };
  }

  return node;
}

function cleanQuestion(question) {
  const newQuestion = pick(
    question,
    getBackendSupportedQuestionProperties(question.type),
  );

  if (newQuestion.answers) {
    let omittedAnswerProperties = [];
    if (NODE_TYPES_SUPPORT_SIMPLE_QUOTAS.includes(question.type)) {
      omittedAnswerProperties = ['quota'];
    }
    // This is something that can happen when going from the old questionnaire
    // to the new. When the old questionnaire gets deprecated we can also deprecate this logic.
    // Currently there is a validation that does not allow an answer type 0 or 2 to have a label defined
    newQuestion.answers = newQuestion.answers.map((answer) => {
      const omittedAnswerPropertiesPerAnswer = [...omittedAnswerProperties];
      if (
        answer.type === ANSWER_NONE_OF_THE_ABOVE ||
        answer.type === ANSWER_DEFAULT
      ) {
        omittedAnswerPropertiesPerAnswer.push('label');
      }

      if (omittedAnswerPropertiesPerAnswer.length > 0) {
        return omit(answer, omittedAnswerPropertiesPerAnswer);
      }

      return answer;
    });
  }

  if (newQuestion.selectedAttributes) {
    const selectedAttributes = newQuestion.selectedAttributes
      .filter((attribute) => attribute.selected)
      .map((attribute) => attribute.refId);

    newQuestion.selectedAttributes =
      selectedAttributes.length > 0 ? selectedAttributes : undefined;
  }

  return handleMinMaxLengthSetting(newQuestion);
}

function cleanQuestions(questions) {
  return questions.map(cleanQuestion);
}

export function unMapNodesAsQuestions(nodes = []) {
  const screeningNodesLength = nodes.filter((node) => node.isScreening).length;

  return nodes.reduce(
    (accumulator, node, index) => {
      if (isGroup(node.type)) {
        const {
          id,
          refId,
          shuffle,
          displaySamePage = false,
          questions,
          concepts,
          attributes,
          properties,
        } = node;

        const newGroup = {
          id,
          groupType: node.type,
          refId,
          shuffle,
          displaySamePage,
          order: index - screeningNodesLength,
          ...(node.alias != null ? { alias: node.alias } : {}),
        };

        if (isAbGroup(node.type)) {
          newGroup.attributeSet = {
            groupings: concepts.map((concept, conceptIndex) => ({
              ...concept,
              attributeValues: attributes.map(
                (attribute) => attribute.values[conceptIndex].refId,
              ),
            })),
            attributes,
            properties,
          };
        } else if (isConjointOrMaxDiffGroup(node.type)) {
          newGroup.attributeSet = {
            attributes: attributes.map((attribute) => {
              const newAttribute = {
                ...attribute,
                values: attribute.values.map((value) =>
                  omit(value, ['isEmpty']),
                ),
              };

              return omit(newAttribute, ['previousValues']);
            }),
            properties,
            groupings: concepts,
            blocks: node.blocks,
            choiceSets: node.choiceSets,
          };
          newGroup.answers = node.answers;
        } else if (isVanWestendorpGroup(node.type)) {
          newGroup.attributeSet = {
            groupings: concepts.map((concept, conceptIndex) => ({
              ...concept,
              attributeValues: attributes.map(
                (attribute) => attribute.values[conceptIndex].refId,
              ),
            })),
            attributes,
            properties,
          };
        } else if (isLoopAndMergeGroup(node.type)) {
          newGroup.attributeSet = loopAndMergeToUpsert(node, nodes);
        }
        accumulator.newQuestions = [
          ...accumulator.newQuestions,
          ...cleanQuestions(questions),
        ];
        accumulator.newGroups.push(newGroup);

        return accumulator;
      }
      accumulator.newQuestions.push(cleanQuestion(node));

      return accumulator;
    },
    { newQuestions: [], newGroups: [] },
  );
}

function loopAndMergeToUpsert(node, nodes) {
  const {
    concepts,
    attributes,
    properties,
    loopAndMergeFromQuestionConfiguration,
  } = node;

  let loopAndMerge;
  if (!isEmpty(loopAndMergeFromQuestionConfiguration)) {
    if (loopAndMergeFromQuestionConfiguration.isProfiling) {
      loopAndMerge = {
        profilingQuestionId:
          loopAndMergeFromQuestionConfiguration.questionRefId,
        ...loopAndMergeFromQuestionConfiguration,
        groupingRelations: (
          attributes.find(isLoopAndMergeAttributeBasedOnQuestion) || {
            values: [],
          }
        ).values.map((value, attributeValueIndex) => ({
          attributeGroupingRefId: concepts[attributeValueIndex].refId,
          profilingOptionId: value.originalAnswerRefId,
        })),

        questionRefId: undefined,
      };
      loopAndMerge = omit(loopAndMerge, ['isProfiling']);
    } else {
      const sourceQuestion = getNodeByRefId(
        nodes,
        node.loopAndMergeFromQuestionConfiguration?.questionRefId,
      );
      const sourceIsOpenEnded = isOpenEndedQuestionType(sourceQuestion?.type);

      loopAndMerge = {
        mergeQuestionRefId: loopAndMergeFromQuestionConfiguration.questionRefId,
        ...loopAndMergeFromQuestionConfiguration,
        groupingRelations: (
          attributes.find(isLoopAndMergeAttributeBasedOnQuestion) || {
            values: [],
          }
        ).values.map((value, attributeValueIndex) => ({
          attributeGroupingRefId: concepts[attributeValueIndex].refId,
          originalAnswerOrScorepointRefId:
            value.originalAnswerRefId || value.originalScorepointRefId,
          relationWith:
            value.originalAnswerRefId || sourceIsOpenEnded
              ? 'answer'
              : 'scorepoint',
        })),

        questionRefId: undefined,
      };
    }
  }

  return {
    groupings: concepts.map((concept, conceptIndex) => ({
      ...concept,
      attributeValues: attributes.map(
        (attribute) => attribute.values[conceptIndex].refId,
      ),
    })),
    attributes,
    properties,
    loopAndMerge,
  };
}
function loopAndMergeFromFetch(node) {
  const { attributeSet = {} } = node;
  const {
    loopAndMerge: loopAndMergeFromQuestionConfiguration,
    properties,
    attributes,
    groupings: concepts,
  } = attributeSet;

  const {
    mergeQuestionRefId,
    profilingQuestionId,
    groupingRelations = [],
  } = loopAndMergeFromQuestionConfiguration || {};

  const carriedDetailsPerAttributeValue = concepts.reduce(
    (acc, { refId, attributeValues = [] }) => ({
      ...acc,
      [attributeValues[0]]: groupingRelations.find(
        (relation) => relation.attributeGroupingRefId === refId,
      ),
    }),
    {},
  );

  return {
    concepts,
    properties,
    attributes: attributes.map((attribute) => {
      if (isLoopAndMergeAttributeBasedOnQuestion(attribute)) {
        return {
          ...attribute,
          values: attribute.values.map((value) => {
            const carriedDetails = carriedDetailsPerAttributeValue[value.refId];
            let originalProperty;
            switch (true) {
              case !isUndefined(carriedDetails.profilingOptionId):
              case carriedDetails.relationWith === 'answer':
                originalProperty = 'originalAnswerRefId';
                break;
              default:
                originalProperty = 'originalScorepointRefId';
                break;
            }
            return {
              ...value,
              [originalProperty]: carriedDetails.profilingOptionId
                ? carriedDetails.profilingOptionId
                : carriedDetails.originalAnswerOrScorepointRefId,
              questionRefId: profilingQuestionId || mergeQuestionRefId,
              isCarriedForward: true,
            };
          }),
        };
      }

      return attribute;
    }),
    loopAndMergeFromQuestionConfiguration:
      prepareLoopAndMergeFromQuestionConfigurationForStore(
        loopAndMergeFromQuestionConfiguration,
      ),
  };
}

export const simplifyQuestions = (questions = []) => {
  const newQuestions = questions.map((question) => {
    const newQuestion = omit(question, [
      PROPERTIES.PROPERTY_PRISTINE_VALUES,
      PROPERTIES.PROPERTY_SHUFFLE,
      PROPERTIES.PROPERTY_TYPE,
      PROPERTIES.PROPERTY_MEDIA,
      PROPERTIES.PROPERTY_IS_CARRIED_FORWARD,
    ]);

    const omittedProperties = [
      PROPERTIES.PROPERTY_HAS_IMAGE,
      PROPERTIES.PROPERTY_MEDIA,
      PROPERTIES.PROPERTY_IS_CARRIED_FORWARD,
      PROPERTIES.PROPERTY_NEXT_Q,
      PROPERTIES.PROPERTY_ORDER,
      PROPERTIES.PROPERTY_STYLE,
      PROPERTIES.PROPERTY_TYPE,
    ];

    if (question.answers) {
      newQuestion.answers = question.answers.map((answer) =>
        omit(answer, omittedProperties),
      );
    }

    if (question.scorepoints) {
      newQuestion.scorepoints = question.scorepoints.map((answer) =>
        omit(answer, omittedProperties),
      );
    }

    return newQuestion;
  });

  return newQuestions;
};

export const simplifyGroups = (groups = []) => {
  const simplifiedGroups = [];
  let currentGroup;

  forEach(groups, (group) => {
    currentGroup = cloneDeep(group);
    if (currentGroup.attributeSet) {
      if (currentGroup.attributeSet.attributes) {
        currentGroup.attributeSet.attributes =
          currentGroup.attributeSet.attributes.map((attribute) => ({
            ...attribute,
            values: attribute.values.map((value) =>
              omit(value, [PROPERTIES.PROPERTY_MEDIA]),
            ),
          }));
      }

      if (currentGroup.attributeSet.groupings) {
        currentGroup.attributeSet.groupings =
          currentGroup.attributeSet.groupings.map((grouping) =>
            omit(grouping, [
              PROPERTIES.PROPERTY_ATTRIBUTE_VALUES,
              PROPERTIES.PROPERTY_MAX_LIMIT,
            ]),
          );
      }

      delete currentGroup.attributeSet.properties;
      delete currentGroup.order;
      delete currentGroup.shuffle;
      delete currentGroup.groupType;

      simplifiedGroups.push(currentGroup);
    }
  });

  return simplifiedGroups;
};

export function getNodeIndex(nodes, groupRefId) {
  if (groupRefId) {
    return nodes.findIndex((node) => node && node.refId === groupRefId);
  }

  const emptyNodeIndex = nodes.findIndex((node) => node === undefined);
  if (emptyNodeIndex > -1) {
    return emptyNodeIndex;
  }

  return nodes.length;
}

export function mapQuestionsAsNodes(questions, groups, showErrors) {
  const nodes = [];

  groups.forEach((group) => {
    if (!group.groupType) {
      throw new Error('missing group type');
    }

    const groupNode = {
      ...generateQuestion(group.groupType, group.refId),
      id: group.id,
      shuffle: group.shuffle,
      displaySamePage: group.displaySamePage,
      ...(group.alias != null ? { alias: group.alias } : {}),
      questions: [],
      pristineValues: defaultPristineValues(!showErrors, group.groupType),
    };

    if (isAbOrVanWestendorpOrConjointOrMaxDiffGroup(group.groupType)) {
      groupNode.concepts = group.attributeSet.groupings;
      groupNode.attributes = group.attributeSet.attributes;
      groupNode.properties = group.attributeSet.properties;
    }

    if (isConjointOrMaxDiffGroup(group.groupType)) {
      groupNode.blocks = group.attributeSet.blocks;
      groupNode.choiceSets = group.attributeSet.choiceSets;
      // needed to start doeSettings calls immediatelly
      groupNode.startUpdatingAfterVersion = 0;
    }

    if (isLoopAndMergeGroup(group.groupType)) {
      const updates = loopAndMergeFromFetch(group);

      Object.assign(
        groupNode,
        { attributeSet: undefined, loopAndMerge: true }, // loopAndMerge property is a temporary fix
        updates,
      );
    }

    nodes[group.order] = groupNode;
  });

  return questions.reduce((accumulator, question) => {
    const { groupRefId } = question;
    const nodeIndex = getNodeIndex(accumulator, groupRefId);
    if (groupRefId) {
      const group = get(accumulator, `[${nodeIndex}]`);
      // group has been added on a previous iteration
      if (group.refId === groupRefId) {
        if (isAbOrVanWestendorpGroup(group.type)) {
          const newQuestion = {
            ...question,
            selectedAttributes: group.attributes.map((attribute) => ({
              refId: attribute.refId,
              selected: !!(
                question.selectedAttributes &&
                question.selectedAttributes.includes(attribute.refId)
              ),
            })),
          };

          group.questions.push(newQuestion);
        } else {
          group.questions.push(question);
        }

        accumulator[nodeIndex] = group;
      } else {
        throw new Error('no group with this groupRefId');
      }

      return accumulator;
    }

    accumulator[nodeIndex] = question;

    return accumulator;
  }, nodes);
}

export function mapQuestionsAndGroupsAsNodes(payload) {
  const {
    questionGroups = [],
    questions: allQuestions = [],
    showErrors = false,
  } = payload;
  const hasGroups = questionGroups.length > 0;
  const grouped = groupBy(allQuestions, PROPERTIES.PROPERTY_IS_SCREENING);
  const screeningQuestions = get(grouped, 'true', []);
  const questions = get(grouped, 'false', []);
  let nodes = handleQuestionsOnFetch(questions, showErrors);

  if (hasGroups) {
    nodes = mapQuestionsAsNodes(nodes, questionGroups, showErrors);
  }

  return { nodes, screeningQuestions };
}

function handleQuestionsOnFetch(questions, showErrors) {
  return questions.map((question) =>
    transformQuestionAfterFetch(question, showErrors),
  );
}

export function transformQuestionAfterFetch(question, showErrors) {
  let updatedQuestion = {
    ...question,
    pristineValues: defaultPristineValues(!showErrors),
  };

  // currently the backend does not store refIds for scorepoints and we need to handle those cases
  if (isMatrixSingleMultipleQuestionType(question.type)) {
    updatedQuestion.scorepoints = question.scorepoints.map((scorepoint) => ({
      ...scorepoint,
      refId: scorepoint.refId || uuid(),
    }));
  }

  if (isSliderQuestionType(question.type)) {
    RENAME_SLIDER_QUESTION_PROPERTIES.forEach(({ source, target }) => {
      if (has(updatedQuestion, source) && !has(updatedQuestion, target)) {
        const sourceValue = get(updatedQuestion, source);

        set(updatedQuestion, target, sourceValue);
        updatedQuestion = omit(updatedQuestion, [source]);
      }
    });
  }

  if ([QUESTION_MATRIX_OPEN_ENDED].includes(question.type)) {
    updatedQuestion.minLength = get(
      question.matrix,
      'validation.openEndedValidation.minLength',
    );
    updatedQuestion.maxLength = get(
      question.matrix,
      'validation.openEndedValidation.maxLength',
    );
  }

  // the backend will not send carried forward data to matrixAnswers
  // but we need them, so i decided to pass them here on fetch and continue as is
  if (
    [
      QUESTION_MATRIX_NUMERIC_OPEN_ENDED,
      QUESTION_MATRIX_OPEN_ENDED,
      QUESTION_MATRIX_CONSTANT_SUM,
    ].includes(question.type)
  ) {
    if (!isEmpty(updatedQuestion.matrix.matrixAnswers)) {
      updatedQuestion.matrix = {
        ...updatedQuestion.matrix,
        matrixAnswers: updatedQuestion.matrix.matrixAnswers.map(
          (matrixAnswer) => ({
            ...matrixAnswer,
            ...getCarriedForwardDataForMatrixAnswer(
              updatedQuestion.answers,
              matrixAnswer,
            ),
          }),
        ),
      };
    }
  }

  return updatedQuestion;
}

export function getMoveModalNodes({
  nodes,
  activeRefId,
  groupRefId,
  includeGroup,
  includeSubQuestions,
  isScreening: isScreeningCheck = false,
  movingToGroup = false,
}) {
  const filteredNodes = nodes.filter(
    (node) => node.isScreening === isScreeningCheck,
  );

  const { previousQuestion } = getTangentQuestions(
    activeRefId,
    filteredNodes,
    groupRefId,
  );

  return filteredNodes.reduce((accumulator, node) => {
    const {
      id,
      refId,
      groupRefId: parentRefId,
      label,
      prompt,
      type,
      questionIndex,
      index,
      isScreening,
      answers,
      matrix,
      carryForward,
    } = node;

    const isCurrent = node.refId === activeRefId;
    const isPrevious = node.refId === previousQuestion;
    const isGroupNode = !!node.questions;
    const nodeIndex = questionIndex;

    if (
      isCurrent ||
      (includeGroup && !isGroupNode && isPrevious) ||
      (movingToGroup && isConversationalType(node.type))
    ) {
      return accumulator;
    }

    if (isGroupNode) {
      let newAccumulator = [...accumulator];

      if (includeGroup && !isPrevious) {
        newAccumulator = accumulator.concat({
          id,
          refId,
          groupRefId: parentRefId,
          label,
          prompt,
          type,
          index,
          nodeIndex,
          carryForward,
        });
      }

      if (
        includeSubQuestions &&
        !isConjointOrMaxDiffGroup(type) &&
        !isVanWestendorpGroup(type)
      ) {
        return newAccumulator.concat(
          getMoveModalNodes({
            nodes: node.questions,
          }),
        );
      }

      return newAccumulator;
    }
    if (isConversationalType(node.type)) {
      return accumulator.concat({
        id,
        refId,
        groupRefId: parentRefId,
        label,
        prompt: replaceMentionedPromptsWithBlank(prompt),
        type,
        index,
        nodeIndex,
        isScreening,
        answers,
        matrix,
        carryForward,
      });
    }

    return accumulator.concat({
      id,
      refId,
      groupRefId: parentRefId,
      label,
      prompt: replaceMentionedPromptsWithBlank(prompt),
      type,
      index,
      nodeIndex,
      isScreening,
      carryForward,
    });
  }, []);
}

export function handleMove({
  source,
  target,
  onMove,
  offset = 0,
  dnDrop = true,
}) {
  const moveOnSameList = source.groupRefId === target.groupRefId;

  if (moveOnSameList) {
    let newIndex;
    if (target.index > source.index) {
      newIndex = target.index + offset - 1;
    } else {
      newIndex = target.index + offset;
    }

    if (source.index === newIndex) {
      return;
    }

    const direction = newIndex - source.index;

    onMove({
      refId: source.id,
      groupRefId: source.groupRefId,
      direction,
      dnDrop,
    });
  } else {
    onMove({
      moveDetails: {
        source: {
          refId: source.id,
          groupRefId: source.groupRefId,
          index: source.index,
        },
        target: {
          refId: target.id,
          groupRefId: target.groupRefId,
          index: target.index,
        },
        offset,
      },
      dnDrop,
    });
  }
}

function getAllReferencedNodes(node) {
  const prompts = getAllNodePromptsWithReferences(node);
  const references = prompts.reduce(
    (accumulator, prompt) => [
      ...accumulator,
      ...getReferencedSources({
        text: prompt,
        flat: true,
      }),
    ],
    [],
  );

  return uniqWith(references, isEqual);
}

function getNodeMentions(sourceNode, mentionType = MENTION_TYPES.QUESTION) {
  const sourceMentions = MENTION_TYPES.QUESTION
    ? getAllReferencedNodes(sourceNode)
    : [];
  const mentionRe =
    mentionType === MENTION_TYPES.QUESTION
      ? new RegExp(`{{refId:${sourceNode.refId}}}`)
      : new RegExp(`{{attrRefId:${sourceNode.refId}}}`);

  return function getMentionsPerNode(node) {
    const nodeMentions = [];

    const hasNodeMentionOnSource = node.prompts.some((prompt) =>
      mentionRe.test(prompt),
    );
    if (hasNodeMentionOnSource) {
      nodeMentions.push({
        refId: node.refId,
        mentionRefId: sourceNode.refId,
        type: mentionType,
      });
    }

    const hasSourceMentionOnNode = sourceMentions.some(
      (mention) => mention.refId === node.refId,
    );
    if (hasSourceMentionOnNode) {
      nodeMentions.push({
        refId: sourceNode.refId,
        mentionRefId: node.refId,
        type: mentionType,
      });
    }

    return nodeMentions;
  };
}

function getSkippedNodeRefIdsDuringMove(
  moveDetails,
  nodes,
  groupNodes,
  previousNodesPerNode,
  ignoredNodes = [],
) {
  const { target, source, offset } = moveDetails;
  const { refId: sourceRefId } = source;
  const { refId: targetRefId, groupRefId: targetGroupRefId } = target;
  let targetNodeRefId = targetRefId || targetGroupRefId;
  const direction = nodes[sourceRefId].index - nodes[targetNodeRefId].index;
  const targetGroupNodes = groupNodes[targetNodeRefId] || [];
  const sourceGroupNodes = groupNodes[sourceRefId] || [];
  const allIgnoredNodes = [...ignoredNodes, ...sourceGroupNodes];
  let refIdsToCheck;

  // moving from bottom to top
  if (direction > 0) {
    // target first child of the group
    targetNodeRefId =
      targetGroupNodes.length > 0 ? targetGroupNodes[0] : targetNodeRefId;
    refIdsToCheck = previousNodesPerNode[sourceRefId].filter(
      (refId) => !refId.includes(VirtualEntryGroupPostfix),
    );
    const nodesToCheckTargetIndex = refIdsToCheck.indexOf(targetNodeRefId);
    refIdsToCheck = refIdsToCheck.slice(nodesToCheckTargetIndex);

    if (offset > 0) {
      refIdsToCheck = refIdsToCheck.slice(1);
    }
    refIdsToCheck.reverse();
  } else {
    // moving from top to bottom
    // target last child of the group
    targetNodeRefId =
      targetGroupNodes.length > 0
        ? targetGroupNodes[targetGroupNodes.length - 1]
        : targetNodeRefId;
    refIdsToCheck = previousNodesPerNode[targetNodeRefId].filter(
      (refId) => !refId.includes(VirtualEntryGroupPostfix),
    );
    const nodesToCheckSourceIndex = refIdsToCheck.indexOf(sourceRefId);
    refIdsToCheck = refIdsToCheck.slice(nodesToCheckSourceIndex + 1);

    if (offset > 0) {
      refIdsToCheck.push(targetNodeRefId);
    }
  }

  if (allIgnoredNodes.length > 0) {
    refIdsToCheck = refIdsToCheck.filter(
      (refId) => !allIgnoredNodes.includes(refId),
    );
  }

  return refIdsToCheck;
}

/**
 * Used during the move of a node to get the nodes that have mentions
 * @param {Object} sourceNode
 * @param {Object} moveDetails
 * @param {Array.<{index: Number, prompt: String}>} nodes
 * @param {Array} groupNodes
 * @param {Array} groupAttributes
 * @param {Object} previousNodesPerNode
 * @param {Array} ignoredNodes
 * @returns {[]|Array.<{refId: String, mentionRefId: String, type: String}>}
 */
export function getNodesWithMentionsOnMove({
  sourceNode,
  moveDetails,
  nodes,
  groupNodes,
  groupAttributes,
  previousNodesPerNode,
  ignoredNodes,
}) {
  const movingNodes = sourceNode.questions
    ? sourceNode.questions
    : [sourceNode];
  const skippedNodeRefIds = getSkippedNodeRefIdsDuringMove(
    moveDetails,
    nodes,
    groupNodes,
    previousNodesPerNode,
    ignoredNodes,
  );

  const mentionsOnQuestions = movingNodes.reduce((accumulator, movingNode) => {
    const getMentionsPerNode = getNodeMentions(movingNode);

    return skippedNodeRefIds.reduce((acc, refId) => {
      if (groupNodes[refId]) {
        return acc;
      }

      return [
        ...acc,
        ...getMentionsPerNode({
          refId,
          prompts: nodes[refId].prompts,
        }),
      ];
    }, accumulator);
  }, []);

  const { source, target } = moveDetails;
  const isMovingInSameEntity = source.groupRefId === target.groupRefId;

  if (isMovingInSameEntity) {
    return mentionsOnQuestions;
  }

  return [
    ...mentionsOnQuestions,
    ...getMentionsOnAttributesForNode({ sourceNode, nodes, groupAttributes }),
  ];
}

export function getMentionsOnAttributesForNode({
  sourceNode,
  nodes,
  groupAttributes,
}) {
  const { refId, groupRefId } = sourceNode;
  const possibleAttributeMentions = groupAttributes[groupRefId];

  if (possibleAttributeMentions) {
    return possibleAttributeMentions.reduce((accumulator, attributeRefId) => {
      const getMentionsPerAttribute = getNodeMentions(
        {
          refId: attributeRefId,
        },
        MENTION_TYPES.LOOP_AND_MERGE,
      );

      return [
        ...accumulator,
        ...getMentionsPerAttribute({
          refId,
          prompts: nodes[refId].prompts,
        }),
      ];
    }, []);
  }

  return [];
}

export function getNodesCarryingAnswersFromNode(sourceNode, nodes = []) {
  return nodes.filter((node) => {
    if (isEmpty(node.carryForward)) {
      return false;
    }
    return some(node.carryForward, ['questionRefId', sourceNode.refId]);
  });
}

// clear invalid mentions & add scroll info
export function clearMentionsFromNodes({
  scrollToRefId,
  nodes,
  nodesToClearMentions,
}) {
  if (!scrollToRefId && nodesToClearMentions.length === 0) {
    return nodes;
  }

  return nodes.map((node) => {
    const { questions, ...restUpdates } = getPromptsWithoutSpecificMentions({
      node,
      nodesToClearMentions,
      scrollToRefId,
    });
    const newNode = {
      ...node,
      ...restUpdates,
    };

    if (node.refId === scrollToRefId) {
      newNode.scrollIntoView = Date.now();
    }

    if (questions) {
      newNode.questions = questions;
    }

    return newNode;
  });
}

export function getRevalidatedNodesAndGlobalErrors({
  nodes,
  errors,
  previousNodesPerNode,
  answersWithLogicFromOutcomes,
  answersWithLogicFromOutcomesHide,
  nodesWithLogicFromOutcomesShow,
  hiddenNodes,
  screeningQuestions,
  groupNode,
  surveyConfig,
}) {
  let globalErrors = errors;

  const newNodes = nodes.map((node) => {
    if (node) {
      let newNode = node;

      ({ newNode, globalErrors } = attachErrorsOnNode({
        node: newNode,
        globalErrors,
        allNodes: nodes,
        previousNodesPerNode,
        answersWithLogicFromOutcomes,
        answersWithLogicFromOutcomesHide,
        nodesWithLogicFromOutcomesShow,
        hiddenNodes,
        screeningQuestions,
        groupNode,
        surveyConfig,
      }));

      let subNodes;
      if (node.questions) {
        ({ newNodes: subNodes, globalErrors } =
          getRevalidatedNodesAndGlobalErrors({
            nodes: node.questions,
            errors: globalErrors,
            previousNodesPerNode,
            answersWithLogicFromOutcomes,
            answersWithLogicFromOutcomesHide,
            nodesWithLogicFromOutcomesShow,
            hiddenNodes,
            screeningQuestions,
            groupNode: node,
            surveyConfig,
          }));
      }

      return {
        ...newNode,
        questions: subNodes,
      };
    }

    return node;
  });

  return { newNodes, globalErrors };
}

export function clearCarryForwardOnNode({
  node,
  source,
  target,
  nodes,
  errors,
  screeningQuestions,
}) {
  let globalErrors = errors;
  let newNode = node;

  const moveInsideGroup = !source.groupRefId && !!target.groupRefId;
  if (moveInsideGroup) {
    if (
      node.refId === source.refId &&
      node.carryForward &&
      node.carryForward.length > 0
    ) {
      newNode = removeAllCarryForward(node);

      // we pass {} for the previousNodesPerNode because is a subNode
      ({ newNode, globalErrors } = attachErrorsOnNode({
        node: newNode,
        globalErrors,
        allNodes: nodes,
        previousNodesPerNode: {},
        screeningQuestions,
      }));
    }
  }

  return {
    newNode,
    globalErrors,
  };
}

export function getMoveDetails(
  { refId, direction, groupRefId },
  nodes,
  source,
) {
  const sourceNode = source || getNodeByRefId(nodes, refId, groupRefId);
  const isMovingInsideSameGroup = groupRefId || sourceNode.groupRefId;
  const nodesToCheck = isMovingInsideSameGroup
    ? getNodeByRefId(nodes, groupRefId || sourceNode.groupRefId).questions
    : nodes;
  const newIndex = sourceNode.index + direction;

  return {
    source: { refId: sourceNode.refId, groupRefId },
    target: {
      refId: nodesToCheck[newIndex].refId,
    },
    offset: direction,
  };
}

export function pickPromptsFromAnswer(answer) {
  return answerPromptProperties.reduce((accumulator, property) => {
    if (answer[property]) {
      return [...accumulator, answer[property]];
    }

    return accumulator;
  }, []);
}

export function getAllNodePromptsWithReferences(node) {
  if (!isNodeSupportAnswersWithMentions(node)) {
    return [node.prompt];
  }

  return nodePropertiesWithPrompt.reduce(
    (accumulator, property) => {
      if (typeof property === 'string' && node[property]) {
        return [
          ...accumulator,
          ...node[property].map(pickPromptsFromAnswer),
        ].flat();
      }

      if (typeof property === 'object' && node[property.name]) {
        return property.values.reduce((acc, nestedProperty) => {
          if (node[property.name][nestedProperty]) {
            return [
              ...acc,
              ...node[property.name][nestedProperty].map(pickPromptsFromAnswer),
            ].flat();
          }

          return acc;
        }, accumulator);
      }

      return accumulator;
    },
    [node.prompt],
  );
}

export function getMinimalNodesRepresentation(nodes) {
  let index = -1;

  return nodes.reduce(
    (accumulator, node) => {
      accumulator.nodes[node.refId] = {
        // eslint-disable-next-line
        index: ++index,
      };

      if (node.questions) {
        accumulator.groupNodes[node.refId] = node.questions.map(getEntityRefId);

        if (isLoopAndMergeGroup(node.type)) {
          accumulator.groupAttributes[node.refId] =
            node.attributes.map(getEntityRefId);
        }

        node.questions.forEach((subNode) => {
          accumulator.nodes[subNode.refId] = {
            prompts: getAllNodePromptsWithReferences(subNode),
            // eslint-disable-next-line
            index: ++index,
          };
        });
      } else {
        accumulator.nodes[node.refId].prompts =
          getAllNodePromptsWithReferences(node);
      }

      return accumulator;
    },
    { nodes: {}, groupNodes: {}, groupAttributes: {} },
  );
}

// when moving more than one question from one group inside an other empty group
// or when we are moving an entire group
// we need to ignore checking for mentions for these nodes
function getIgnoredNodes(sourceRefId, groupNodes, movements) {
  if (groupNodes[sourceRefId]) {
    return groupNodes[sourceRefId];
  }

  if (movements.length === 1) {
    return [];
  }

  return movements.reduce((accumulator, movement) => {
    if (sourceRefId !== movement.source.refId) {
      return [...accumulator, movement.source.refId];
    }

    return accumulator;
  }, []);
}

function isRecallAllowedOnTarget(allNodes, refId) {
  const targetNode = getNodeByRefId(allNodes, refId);

  if (!targetNode) {
    return true;
  }

  if (!isGroup(targetNode.type)) {
    return false;
  }

  if (targetNode.displaySamePage || targetNode.shuffle > 0) {
    return false;
  }

  return true;
}

export function isGroupAllowedToHaveQuestionWithQuotas(allNodes, refId) {
  const targetNode = getNodeByRefId(allNodes, refId);

  if (!targetNode) {
    return true;
  }
  if (!isGroup(targetNode.type)) return false;

  // advanced quotas require more fine-grained checks
  const isAdvancedQuotaAllowedInGroup =
    GROUP_TYPES_ALLOW_ADVANCED_QUOTAS.includes(targetNode.type) &&
    !targetNode.displaySamePage &&
    targetNode.shuffle === SHUFFLE_NONE;

  return {
    simple: GROUP_TYPES_ALLOW_SIMPLE_QUOTAS.includes(targetNode.type),
    advanced: isAdvancedQuotaAllowedInGroup,
  };
}

function subnodeHasOwnLogicLostOnGroupShuffleOrSamePage(ql, subNodeRefId) {
  if (!ql?.nodeStatements) {
    return false;
  }

  const subNodeStatements =
    questionnaireLogicService.findNodeStatementsByNodeId(ql, subNodeRefId);

  if (!subNodeStatements?.statements?.length) {
    return false;
  }

  const statementsWithRules =
    questionnaireLogicService.getStatementsWithRule(subNodeStatements);

  if (statementsWithRules.length > 0) {
    const onlySimpleQuota = statementsWithRules.every(
      (statement) =>
        get(statement, 'rule.quota.context.type') ===
        QuotaConditionTypeEnum.Simple,
    );

    return !onlySimpleQuota;
  }

  const passthroughStatement =
    questionnaireLogicService.findPassthroughStatementByNode(ql, subNodeRefId);
  const { nextNodeId } = Questionnaire.getNodeInfo(
    get(ql, 'questionnaire'),
    subNodeRefId,
  );

  if (!passthroughStatement) {
    return true;
  }

  return passthroughStatement.outcome.params.id !== nextNodeId;
}

export function willEnablingGroupShuffleOrSamePageLoseSubnodeLogic({
  groupNode,
  ql,
}) {
  return (groupNode.questions || []).some(({ refId: subNodeRefId }) =>
    subnodeHasOwnLogicLostOnGroupShuffleOrSamePage(ql, subNodeRefId),
  );
}

function statementRuleReferencesNodes(statement, ownerNodeId, nodeRefIdSet) {
  if (!statement?.rule) {
    return false;
  }

  const referencedNodeIds = ruleService.getReferencedNodeIds(
    statement.rule,
    ownerNodeId,
  );
  return referencedNodeIds.some((id) => nodeRefIdSet.has(id));
}

function getGroupStatementsWithRules(ql, groupRefId) {
  if (!ql?.nodeStatements) {
    return [];
  }

  const groupNodeStatements =
    questionnaireLogicService.findNodeStatementsByNodeId(ql, groupRefId);

  if (!groupNodeStatements) {
    return [];
  }

  return questionnaireLogicService.getStatementsWithRule(groupNodeStatements);
}

export function willConvertingGroupToLoopAndMergeLoseQuotas({
  groupNode,
  ql,
  quotasMinimalMap = {},
}) {
  if (!isBasicGroup(groupNode?.type)) {
    return false;
  }

  const subNodeRefIds = (groupNode.questions || []).map(
    ({ refId: subNodeRefId }) => subNodeRefId,
  );

  if (subNodeRefIds.length === 0) {
    return false;
  }

  const willRemoveSubnodeSimpleQuotas =
    !GROUP_TYPES_ALLOW_SIMPLE_QUOTAS.includes(GROUP_LOOP_MERGE) &&
    subNodeRefIds.some(
      (subNodeRefId) => quotasMinimalMap[subNodeRefId]?.simpleQuotasPresent,
    );

  const willRemoveSubnodeAdvancedQuotas =
    !GROUP_TYPES_ALLOW_ADVANCED_QUOTAS.includes(GROUP_LOOP_MERGE) &&
    subNodeRefIds.some(
      (subNodeRefId) => quotasMinimalMap[subNodeRefId]?.advancedQuotasPresent,
    );

  const willRemoveGroupRulesReferencingSubnodes =
    groupHasRulesReferencingSubnodes(ql, groupNode.refId, subNodeRefIds);

  return (
    willRemoveSubnodeSimpleQuotas ||
    willRemoveSubnodeAdvancedQuotas ||
    willRemoveGroupRulesReferencingSubnodes
  );
}

export function groupHasRulesReferencingSubnodes(
  ql,
  groupRefId,
  subNodeRefIds,
) {
  const subNodeRefIdSet = new Set(subNodeRefIds);
  const statementsWithRules = getGroupStatementsWithRules(ql, groupRefId);

  return statementsWithRules.some((statement) =>
    statementRuleReferencesNodes(statement, groupRefId, subNodeRefIdSet),
  );
}

export function removeGroupStatementsReferencingSubnodes(
  ql,
  groupRefId,
  subNodeRefIds,
) {
  const subNodeRefIdSet = new Set(subNodeRefIds);
  const statementsWithRules = getGroupStatementsWithRules(ql, groupRefId);

  let newQl = ql;
  statementsWithRules.forEach((statement) => {
    if (!statementRuleReferencesNodes(statement, groupRefId, subNodeRefIdSet)) {
      return;
    }

    newQl = questionnaireLogicService.deleteRule(
      newQl,
      groupRefId,
      statement.id,
      statement.rule.id,
      true,
    );
  });

  return newQl;
}

function getAllMentionsForNode(node) {
  const mentions = getAllReferencedNodes(node);

  return mentions.reduce(
    (accumulator, mention) => [
      ...accumulator,
      { refId: node.refId, mentionRefId: mention.refId },
    ],
    [],
  );
}

const isNodeReferencedByOtherNodesRules = (ql, refId) => {
  const refIdSet = new Set([refId]);
  return (ql?.nodeStatements || []).some(({ nodeId, statements }) =>
    statements.some((statement) =>
      statementRuleReferencesNodes(statement, nodeId, refIdSet),
    ),
  );
};

// Uses nodesWithNonScreeningLogic so a screening question whose only logic is
// its own screening navigation does not ask for confirmation. The quota-only
// lists only describe the question's own statements, so they don't excuse a
// question whose answers other questions' rules read.
export function hasLogicLostOnNodeRemove({
  refId,
  ql,
  nodesWithNonScreeningLogic = [],
  nodesWithSimpleQuotaOnlyLogic = [],
  nodesWithAdvancedQuotaOnlyLogic = [],
}) {
  if (!nodesWithNonScreeningLogic.includes(refId)) {
    return false;
  }

  const hasQuotaOnlyLogic =
    nodesWithSimpleQuotaOnlyLogic.includes(refId) ||
    nodesWithAdvancedQuotaOnlyLogic.includes(refId);

  return !hasQuotaOnlyLogic || isNodeReferencedByOtherNodesRules(ql, refId);
}

export function validateNodesOnMove({
  movements,
  allNodes,
  nodesWithLogic,
  nodesWithSimpleQuotaOnlyLogic = [],
  nodesWithAdvancedQuotaOnlyLogic = [],
  previousNodesPerNode,
}) {
  let isMovedNodeUsedForCarryingForward = 0;
  let carryForward = 0;
  let logic = 0;
  let hasSimpleQuotaOnlyLogic = true;
  let hasAdvancedQuotaOnlyLogic = true;
  let nodesWithPreviousRevisionCarryForward = 0;
  let nodesWithPreviousRevisionCarryForwardUsed = 0;
  let nodesToClearMentions = [];
  const nodesAffected = [];
  let canKeepSimpleQuotas = true;
  let canKeepAdvancedQuotas = true;
  const addNodeLabel = (nodeLabel) => {
    if (nodeLabel && !nodesAffected.includes(nodeLabel)) {
      nodesAffected.push(nodeLabel);
    }
  };
  const { nodes, groupNodes, groupAttributes } =
    getMinimalNodesRepresentation(allNodes);

  movements.forEach((moveDetails) => {
    const { refId: sourceRefId, groupRefId: sourceGroupRefId } =
      moveDetails.source;
    const { groupRefId: targetGroupRefId } = moveDetails.target;
    const sourceNode = getNodeByRefId(allNodes, sourceRefId, sourceGroupRefId);
    const sourceLabel = getDisplayLabelWithGroupPrefix(sourceNode, allNodes);
    const isRecallAllowed = isRecallAllowedOnTarget(allNodes, targetGroupRefId);
    const {
      simple: isSimpleQuotaAllowedCurrent,
      advanced: isAdvancedQuotaAllowedCurrent,
    } = isGroupAllowedToHaveQuestionWithQuotas(allNodes, targetGroupRefId);

    const nodeMentions = isRecallAllowed
      ? getNodesWithMentionsOnMove({
          sourceNode,
          moveDetails,
          nodes,
          groupNodes,
          groupAttributes,
          previousNodesPerNode,
          ignoredNodes: getIgnoredNodes(sourceRefId, groupNodes, movements),
        })
      : getAllMentionsForNode(sourceNode);

    const nodesForwardingAnswersFromNode = getNodesCarryingAnswersFromNode(
      sourceNode,
      allNodes,
    );

    if (nodeMentions.length > 0) {
      addNodeLabel(sourceLabel);
      nodesToClearMentions = [...nodesToClearMentions, ...nodeMentions];
    }

    if (nodesForwardingAnswersFromNode.length > 0) {
      addNodeLabel(sourceLabel);
      isMovedNodeUsedForCarryingForward += 1;
    }

    if (sourceNode.carryForward && sourceNode.carryForward.length > 0) {
      addNodeLabel(sourceLabel);
      carryForward += 1;
    }

    if (nodesWithLogic.some((id) => id === sourceRefId)) {
      addNodeLabel(sourceLabel);
      logic += 1;
    }

    if (!nodesWithSimpleQuotaOnlyLogic.includes(sourceRefId)) {
      hasSimpleQuotaOnlyLogic = false;
    }
    if (!isSimpleQuotaAllowedCurrent) {
      canKeepSimpleQuotas = false;
    }

    if (!nodesWithAdvancedQuotaOnlyLogic.includes(sourceRefId)) {
      hasAdvancedQuotaOnlyLogic = false;
    }
    if (!isAdvancedQuotaAllowedCurrent) {
      canKeepAdvancedQuotas = false;
    }

    if (itemHasCarryForwardFromPreviousRevision({ itemToCheck: sourceNode })) {
      addNodeLabel(sourceLabel);
      nodesWithPreviousRevisionCarryForward += 1;
    }

    if (
      itemIsUsedInCarryForwardFromPreviousRevision({
        nodes: allNodes,
        itemToCheck: sourceNode,
      })
    ) {
      addNodeLabel(sourceLabel);
      nodesWithPreviousRevisionCarryForwardUsed += 1;
    }
  });

  return {
    nodesAffected: nodesAffected.sort((labelA, labelB) =>
      String(labelA).localeCompare(String(labelB), undefined, {
        numeric: true,
      }),
    ),
    nodesToClearMentions,
    hasRecall: nodesToClearMentions.length > 0,
    hasCarryForward: carryForward > 0,
    hasSourceCarryForward: isMovedNodeUsedForCarryingForward > 0,
    hasNodesWithPreviousRevisionCarryForward:
      nodesWithPreviousRevisionCarryForward > 0,
    hasNodesWithPreviousRevisionCarryForwardUsed:
      nodesWithPreviousRevisionCarryForwardUsed > 0,
    hasLogic: logic > 0,
    hasSimpleQuotaOnlyLogic,
    canKeepSimpleQuotas,
    hasAdvancedQuotaOnlyLogic,
    canKeepAdvancedQuotas,
  };
}

const findRootRefIds = ({ questions, questionRefId }) => {
  const originalRefIds = [];

  const searchRefId = (refId) => {
    questions.forEach((question) => {
      if (!question.answers) return;
      const carried = question.answers
        .concat(question.scorepoints || [])
        .find((answer) => answer.refId === refId && answer.isCarriedForward);

      if (carried) {
        searchRefId(
          carried.originalAnswerRefId || carried.originalScorepointRefId,
        );
      }
    });
    originalRefIds.push(refId);
  };

  const currentQuestion = questions.find(
    (question) => question.refId === questionRefId,
  );

  if (currentQuestion) {
    if (!currentQuestion.answers) return [];
    currentQuestion.answers
      .concat(currentQuestion.scorepoints || [])
      .filter((answer) => answer.isCarriedForward)
      .forEach((answer) =>
        searchRefId(
          answer.originalAnswerRefId || answer.originalScorepointRefId,
        ),
      );
  }

  return originalRefIds;
};

function hasDuplicates(array) {
  return new Set(array).size !== array.length;
}

function getCarryForwardErrors({ allNodes, node }) {
  if (!allNodes || !node) return {};

  if (node.carryForward) {
    const rootIds = findRootRefIds({
      questions: allNodes,
      questionRefId: node.refId,
    });
    if (hasDuplicates(rootIds)) return { duplicateCarryForwardAnswer: true };
  }

  return {};
}

export function attachErrorsOnNode({
  node,
  globalErrors,
  allNodes,
  screeningQuestions,
  previousNodesPerNode,
  answersWithLogicFromOutcomes,
  answersWithLogicFromOutcomesHide,
  nodesWithLogicFromOutcomesShow,
  hiddenNodes,
  groupNode,
  surveyConfig,
}) {
  const errors = getNodeErrors({
    node,
    nodes: allNodes,
    screeningQuestions,
    previousNodesPerNode,
    answersWithLogicFromOutcomes,
    answersWithLogicFromOutcomesHide,
    nodesWithLogicFromOutcomesShow,
    hiddenNodes,
    groupNode,
    surveyConfig,
  });

  const carryForwardErrors = getCarryForwardErrors({
    allNodes,
    node,
  });

  if (globalErrors) {
    return {
      newNode: {
        ...node,
        errors: { ...errors, ...carryForwardErrors },
      },
      globalErrors: updateQuestionnaireErrors({
        globalErrors,
        nodeErrors: { ...errors, ...carryForwardErrors },
        nodeRefId: node.refId,
        groupRefId: node.groupRefId,
        nodeType: node.type,
      }),
    };
  }

  return {
    newNode: {
      ...node,
      errors: { ...errors, ...carryForwardErrors },
    },
  };
}

export function attachErrorsOnNodes({
  nodes,
  previousNodesPerNode,
  answersWithLogicFromOutcomes,
  answersWithLogicFromOutcomesHide,
  nodesWithLogicFromOutcomesShow,
  hiddenNodes,
  errors,
  clearPristineValues = false,
  previousNodes,
  screeningQuestions,
  allNodesForValidation,
  groupNode,
  surveyConfig,
}) {
  let globalErrors = errors;
  const resolvedAllNodes =
    allNodesForValidation ??
    (screeningQuestions?.length ? [...screeningQuestions, ...nodes] : nodes);
  const newNodes = nodes.map((node, index) => {
    const ignoreSameNodes = previousNodes && previousNodes[index] === node;
    if (ignoreSameNodes) {
      return node;
    }

    let newNode = {
      ...node,
    };

    if (clearPristineValues) {
      newNode.pristineValues = defaultPristineValues(false, newNode.type);
    }

    ({ newNode, globalErrors } = attachErrorsOnNode({
      node: newNode,
      globalErrors,
      allNodes: resolvedAllNodes,
      previousNodesPerNode,
      answersWithLogicFromOutcomes,
      answersWithLogicFromOutcomesHide,
      nodesWithLogicFromOutcomesShow,
      hiddenNodes,
      screeningQuestions,
      groupNode,
      surveyConfig,
    }));

    let subNodes;
    if (node.questions) {
      ({ newNodes: subNodes, globalErrors } = attachErrorsOnNodes({
        nodes: node.questions,
        previousNodesPerNode,
        errors: globalErrors,
        clearPristineValues,
        previousNodes: previousNodes && previousNodes[index].questions,
        answersWithLogicFromOutcomes,
        answersWithLogicFromOutcomesHide,
        nodesWithLogicFromOutcomesShow,
        hiddenNodes,
        screeningQuestions,
        allNodesForValidation: resolvedAllNodes,
        groupNode: node,
        surveyConfig,
      }));

      newNode.questions = subNodes;
    }

    return newNode;
  });

  return { newNodes, globalErrors };
}

export function attachAIGlobalErrors(questionnaireErrors = [], nodes = []) {
  // remove previous global errors
  const currentErrors = reject(questionnaireErrors, [
    'questionRefId',
    undefined,
  ]);

  // additional checks for global errors
  // mainly due to AI not respecting question limits
  const maxDiffQuestionCount = nodes.filter(isMaxDiffGroupNode).length;
  const conjointQuestionCount = nodes.filter(isConjointGroupNode).length;
  const abTestQuestionCount = nodes.filter(isAbGroupNode).length;
  const conversationalAiQuestionCount =
    nodes.filter(isConversationalNode).length;

  if (maxDiffQuestionCount + conjointQuestionCount > 1) {
    currentErrors.push({
      questionRefId: undefined,
      errors: {
        tooManyMaxDiffOrConjointQuestions: true,
      },
    });
  }
  if (abTestQuestionCount > 1) {
    currentErrors.push({
      questionRefId: undefined,
      errors: {
        tooManyAbTestQuestions: true,
      },
    });
  }
  if (conversationalAiQuestionCount > MAX_CONVERSATIONAL_AI_QUESTIONS) {
    currentErrors.push({
      questionRefId: undefined,
      errors: {
        tooManyConversationalAiQuestions: true,
      },
    });
  }

  return currentErrors;
}

export function isThereAnyPreviousQuestionOnNodesList(nodes, questionRefId) {
  const index = findIndex(nodes, ['refId', questionRefId]);
  let questionExists = false;
  let prevIndex = index - 1;

  for (prevIndex; prevIndex >= 0; prevIndex -= 1) {
    if (!isGroup(nodes[prevIndex].type)) {
      questionExists = true;
      break;
    }
  }

  return questionExists;
}

export function getABGroupAttributesForQuestion(question, nodes) {
  if (question.groupRefId) {
    const group = getNodeByRefId(nodes, question.groupRefId);

    if (group && isAbGroup(group.type)) {
      return group.attributes;
    }
  }

  return [];
}

export function getFirstQuestionIdWithError(questions = [], errors) {
  if (isEmpty(errors)) {
    return null;
  }
  const copiedErrors = [...errors];
  // sort errors by question index
  // sort mutates original array and state mutation is forbidden
  const sortedErrors = copiedErrors.sort((a, b) => {
    // the errors array can have questionnaire errors or logic errors
    // the logic errors key for id is 'nodeId'
    // while for the questionnaire validation errors (e.g empty prompt)
    // the key for id is 'questionRefId'
    const nodeAId = a.questionRefId || a.nodeId;
    const nodeBId = b.questionRefId || b.nodeId;
    const nodeA = getNodeByRefId(questions, nodeAId, a.groupRefId);
    const nodeB = getNodeByRefId(questions, nodeBId, b.groupRefId);

    // not found node
    if (!nodeA || !nodeB) {
      return -1;
    }

    return parseFloat(nodeA.questionIndex) - parseFloat(nodeB.questionIndex);
  });

  const firstError = sortedErrors[0];

  return {
    refId: firstError.questionRefId || firstError.nodeId,
    groupRefId: firstError.groupRefId,
  };
}

export function getFirstSkippedQuestionId(nodes = []) {
  const skippedNode = getNodeDeepByCondition(nodes, (node) => node.isUnused);

  return skippedNode.refId;
}

export function isDoeVersionNeedsToBeUpdated(
  previousCombinations,
  updatedCombinations,
) {
  const addedOrRemovedCombinations = xor(
    updatedCombinations,
    previousCombinations,
  );
  const atLeastOneCombinationAddedOrRemoved = addedOrRemovedCombinations.some(
    (combination) => combination.length > 1,
  );

  return atLeastOneCombinationAddedOrRemoved;
}

function updateGroupPropertiesInternal(node, newProperties, allNodes) {
  const properties = {
    ...newProperties,
  };

  if (isConjointOrMaxDiffGroup(node.type) && properties.excludedCombinations) {
    const { excludedCombinations, doeStructureVersion } = node.properties;

    return {
      ...node.properties,
      ...properties,
      doeStructureVersion: isDoeVersionNeedsToBeUpdated(
        excludedCombinations,
        properties.excludedCombinations,
      )
        ? doeStructureVersion + 1
        : doeStructureVersion,
    };
  }

  // manually invalidate doe when we have more than 13 levels
  // in order to set "Alternatives per choice set" to 4
  if (
    isMaxDiffGroup(node.type) &&
    isUndefined(properties.customChoiceSetSize)
  ) {
    // maxdiff only one attribute
    const maxAttributeValuesLength = getMaxAttributeValuesLimit(1);
    const attributeValuesLength = getAttributeValuesLength(node.attributes);

    // when we have more than 13 levels
    if (attributeValuesLength > maxAttributeValuesLength) {
      if (
        !node.properties.customChoiceSetSize ||
        (node.properties.customChoiceSetSize &&
          node.properties.customChoiceSetSize >
            DOE_MAX_MANUAL_VALUE_WHEN_MAX_LEVELS)
      ) {
        properties.customChoiceSetSize = DOE_MAX_MANUAL_VALUE_WHEN_MAX_LEVELS;
      }
    }
  }

  if (
    isConjointOrMaxDiffGroup(node.type) &&
    !isUndefined(properties.customChoiceSetSize)
  ) {
    const { doeStructureVersion, choiceSetSize } = node.properties;

    const isDoeVersionNeedsUpdate =
      choiceSetSize !== properties.customChoiceSetSize;

    return {
      ...node.properties,
      ...properties,
      doeStructureVersion: isDoeVersionNeedsUpdate
        ? doeStructureVersion + 1
        : doeStructureVersion,
    };
  }
  if (
    isLoopAndMergeGroup(node.type) &&
    properties.userDefinedRepetitions === false
  ) {
    properties.repetitions = getMaxLoopAndMergeRepetitions(node, allNodes);
  }

  return {
    ...node.properties,
    ...properties,
  };
}

export function updateGroupProperties({
  refId,
  nodes,
  properties,
  errors,
  partialPristineValues,
  previousNodesPerNode,
}) {
  let globalErrors = errors;
  const newNodes = nodes.map((node) => {
    if (node.refId === refId) {
      let newNode = {
        ...node,
        properties: updateGroupPropertiesInternal(node, properties, nodes),
      };

      if (partialPristineValues) {
        newNode.pristineValues = {
          ...newNode.pristineValues,
          ...partialPristineValues,
        };
      }

      if (globalErrors) {
        ({ newNode, globalErrors } = attachErrorsOnNode({
          node: newNode,
          allNodes: nodes,
          globalErrors: errors,
          previousNodesPerNode,
        }));
      }

      return newNode;
    }

    return node;
  });

  return { newNodes, globalErrors };
}

export function updateGroupPropertyValues(nodes, payload, stateErrors) {
  const { refId, attributeRefId, attributeValueRefId, media, ...restPayload } =
    payload;
  let globalErrors = stateErrors;

  const newNodes = nodes.map((node) => {
    if (node.refId === refId) {
      const newAttributes = node.attributes.map((attribute) => {
        if (attribute.refId === attributeRefId) {
          return {
            ...attribute,
            values: attribute.values.map((attributeValue) => {
              if (attributeValue.refId === attributeValueRefId) {
                return {
                  ...attributeValue,
                  media: isEmpty(media) ? undefined : media,
                  ...restPayload,
                };
              }

              return attributeValue;
            }),
          };
        }

        return attribute;
      });

      let newNode = {
        ...node,
        attributes: newAttributes,
      };

      if (stateErrors) {
        ({ newNode, globalErrors } = attachErrorsOnNode({
          node: newNode,
          allNodes: nodes,
          globalErrors,
        }));
      }

      return newNode;
    }

    return node;
  });

  return {
    newNodes,
    globalErrors,
  };
}

export function updateConjointOrMaxDiffPropertiesOnRemove({
  properties,
  attributeValueRefIds,
  cleanExcludedCombinations = false,
}) {
  return {
    ...properties,
    excludedCombinations: cleanExcludedCombinations
      ? []
      : properties.excludedCombinations.map((combination) =>
          combination.filter(
            (combinationRefId) =>
              !attributeValueRefIds.includes(combinationRefId),
          ),
        ),
  };
}

export function hasDemographicTaxonomyWithLogic(
  demographicKey,
  taxonomyWithLogic,
  value,
) {
  const { taxonomyWithQuestionnaireLogic, taxonomyWithDisqualificationLogic } =
    taxonomyWithLogic;
  const taxonomyQuestionnaireLogic =
    taxonomyWithQuestionnaireLogic[demographicKey];
  const taxonomyDisqualificationLogic =
    taxonomyWithDisqualificationLogic[demographicKey];
  let hasTaxonomyQuestionnaireLogic = !!taxonomyQuestionnaireLogic;
  let hasTaxonomyDisqualificationLogic = !!taxonomyDisqualificationLogic;

  if (value) {
    hasTaxonomyQuestionnaireLogic =
      taxonomyQuestionnaireLogic && taxonomyQuestionnaireLogic.includes(value);
    hasTaxonomyDisqualificationLogic =
      hasTaxonomyDisqualificationLogic &&
      taxonomyDisqualificationLogic.includes(value);
  }

  return {
    hasTaxonomyQuestionnaireLogic,
    hasTaxonomyDisqualificationLogic,
    hasTaxonomyLogic:
      hasTaxonomyQuestionnaireLogic || hasTaxonomyDisqualificationLogic,
  };
}

// case class DoeAttribute(ref_id: String, name: Option[String], levels: Seq[DoeAttributeLevel])
// case class DoeAttributeLevel(ref_id: String, name: Option[String])
function prepareAttributesForDoe(conjoint) {
  const attributes = [];

  conjoint.attributes.forEach((attribute) => {
    const attributeLevels = [];
    attribute.values.forEach((attributeValue) => {
      attributeLevels.push({
        refId: attributeValue.refId,
        name: attributeValue.text,
      });
    });
    attributes.push({
      refId: attribute.refId,
      name: attribute.name,
      levels: attributeLevels,
    });
  });
  return attributes;
}

// case class DoeExcludedCombination(excludedLevels: List[DoeExcludedAttributeLevel])
// case class DoeExcludedAttributeLevel(attributeRefId: String, attributeLevelRefId: String)
function prepareExcludedCombinationsForDoe(conjoint) {
  const attributesByLevelRefId = {};
  conjoint.attributes.forEach((attribute) => {
    attribute.values.forEach((attributeValue) => {
      attributesByLevelRefId[attributeValue.refId] = attribute.refId;
    });
  });
  const excludedCombinations = get(
    conjoint,
    'properties.excludedCombinations',
    [],
  );
  const transformedExcludedCombinations = [];
  excludedCombinations.forEach((combination) => {
    const excludedCombination = [];
    combination.forEach((attributeLevelRefId) => {
      excludedCombination.push({
        attributeRefId: attributesByLevelRefId[attributeLevelRefId],
        attributeLevelRefId,
      });
    });
    transformedExcludedCombinations.push(excludedCombination);
  });
  return transformedExcludedCombinations;
}

export function prepareAttributesAndExcludedCombinationsForDoe(conjoint) {
  return {
    attributes: prepareAttributesForDoe(conjoint),
    excludedCombinations: prepareExcludedCombinationsForDoe(conjoint),
  };
}

export const duplicateQuestion = (questionData) => {
  const newGroupRefId = isGroup(questionData.type) ? uuid() : undefined;
  const isBasicTypeGroup = isBasicGroup(questionData.type);
  const isLoopAndMergeTypeGroup = isLoopAndMergeGroup(questionData.type);
  const isVanWestendorpTypeGroup = isVanWestendorpGroup(questionData.type);
  const newRelatedQuestions = [uuid(), uuid()];
  const isBipolar = isBipolarQuestionType(questionData.type);
  const isMatrixNumericOpenEnded = isMatrixNumericQuestionType(
    questionData.type,
  );
  const isMatrixOpenEnded = isMatrixOpenEndedQuestionType(questionData.type);
  const isConstantSum = isMatrixConstantSumType(questionData.type);
  const isConversational = isConversationalType(questionData.type);
  const hasMatrix =
    isBipolar ||
    isMatrixOpenEnded ||
    isMatrixNumericOpenEnded ||
    isConstantSum ||
    isConversational;

  const isDrillDown = isDrillDownQuestionType(questionData.type);
  const propertyReplaceMap = {
    id: undefined,
    alias: undefined,
    tickId: undefined,
    isFavorite: undefined,
    quotaAssignmentStrategy: undefined,
    quotaLimitType: undefined,
    refId: (refId, context, propertyPath) => {
      if (
        isVanWestendorpTypeGroup &&
        (propertyPath === 'questions.1.refId' ||
          propertyPath === 'questions.2.refId')
      ) {
        const questionIndex = propertyPath.split('.')[1];
        return newRelatedQuestions[questionIndex - 1];
      }
      // use the newGroupRefId in scope only on group node
      if (isGroup(context.type) && refId === questionData.refId) {
        return newGroupRefId;
      }

      if (startsWith(propertyPath, PROPERTIES.PROPERTY_ATTRIBUTES_SELECTED)) {
        return refId;
      }

      return uuid();
    },
    selected: (selectedValue, context, propertyPath) => {
      if (startsWith(propertyPath, PROPERTIES.PROPERTY_ATTRIBUTES_SELECTED)) {
        return true;
      }
      return selectedValue;
    },
    groupRefId: (groupRefId, context) =>
      isGroup(context.type) ? undefined : newGroupRefId || groupRefId,
    // [TODO:] property attributeValues of AB concepts is not getting
    // updated with the new refIds that were created for the attributes
    // only applicable when we get more than one AB group in a single survey
    relatedQuestions: (relatedQuestionsArray = []) => {
      if (relatedQuestionsArray.length === 2) {
        return newRelatedQuestions;
      }
      return [];
    },
  };

  let transformedQuestion = transformPropsDeep(
    questionData,
    propertyReplaceMap,
    {},
  );

  // second pass if VanWestendorp group to update recall
  // in Newton Miller questions
  if (isVanWestendorpTypeGroup) {
    const vanWestendorpQuestion = find(questionData.questions, [
      'type',
      QUESTION_VAN_WESTENDORP,
    ]);
    const duplicatedVanWestendorpQuestion = find(
      transformedQuestion.questions,
      ['type', QUESTION_VAN_WESTENDORP],
    );

    const vwPropertyReplaceMap = {
      prompt: (prompt) => {
        const newPrompt = replaceVanWestendorpAnswerRecalls(
          prompt,
          vanWestendorpQuestion,
          duplicatedVanWestendorpQuestion,
        );
        return newPrompt;
      },
      htmlPrompt: (htmlPrompt) => {
        const newHtmlPrompt = replaceVanWestendorpAnswerRecalls(
          htmlPrompt,
          vanWestendorpQuestion,
          duplicatedVanWestendorpQuestion,
        );
        return newHtmlPrompt;
      },
    };
    transformedQuestion = transformPropsDeep(
      transformedQuestion,
      vwPropertyReplaceMap,
      {},
    );
  }

  if (isBasicTypeGroup || isLoopAndMergeTypeGroup) {
    const nodesInGroup = get(questionData, PROPERTIES.PROPERTY_QUESTIONS, []);
    const duplicatedNodesInGroup = nodesInGroup
      .map(duplicateQuestion)
      .map((node) => ({ ...node, groupRefId: newGroupRefId }));
    transformedQuestion.questions = duplicatedNodesInGroup;
  }

  // handle selected attributes for VW question group
  if (isVanWestendorpTypeGroup) {
    const originalAttributes = get(
      questionData,
      PROPERTIES.PROPERTY_ATTRIBUTES,
      [],
    );
    const duplicatedAttributes = get(
      transformedQuestion,
      PROPERTIES.PROPERTY_ATTRIBUTES,
      [],
    );

    const originalToDuplicateMap = originalAttributes.reduce(
      (map, attribute, index) => ({
        ...map,
        [attribute.refId]: duplicatedAttributes[index].refId,
      }),
      {},
    );

    forEach(questionData.questions, (originalSubQuestion, questionIndex) => {
      const duplicateSubQuestion = transformedQuestion.questions[questionIndex];

      originalSubQuestion.selectedAttributes.forEach(
        (selectedAttribute, index) => {
          duplicateSubQuestion.selectedAttributes[index].refId =
            originalToDuplicateMap[selectedAttribute.refId];
        },
      );
    });
  }

  // handle selected attributes for loop and merge question group
  if (isLoopAndMergeTypeGroup) {
    const loopAndMergeAttributes = get(questionData, 'attributes', []);
    const duplicatedLoopAndMergeAttributes = get(
      transformedQuestion,
      'attributes',
      [],
    );

    const loopAndMergePropertyReplaceMap = {
      prompt: (prompt) => {
        const newPrompt = replaceLoopAndMergeAnswerRecalls(
          prompt,
          loopAndMergeAttributes,
          duplicatedLoopAndMergeAttributes,
        );
        return newPrompt;
      },
      htmlPrompt: (htmlPrompt) => {
        const newHtmlPrompt = replaceLoopAndMergeAnswerRecalls(
          htmlPrompt,
          loopAndMergeAttributes,
          duplicatedLoopAndMergeAttributes,
        );
        return newHtmlPrompt;
      },
      answers: (answers) =>
        answers.map((answer) => ({
          ...answer,
          body: replaceLoopAndMergeAnswerRecalls(
            answer.body,
            loopAndMergeAttributes,
            duplicatedLoopAndMergeAttributes,
          ),
        })),
      scorepoints: (scorepoints) =>
        scorepoints.map((scorepoint) => ({
          ...scorepoint,
          body: replaceLoopAndMergeAnswerRecalls(
            scorepoint.body,
            loopAndMergeAttributes,
            duplicatedLoopAndMergeAttributes,
          ),
        })),
      beforeLabel: (beforeLabel) => {
        const newBeforeLabel = replaceLoopAndMergeAnswerRecalls(
          beforeLabel,
          loopAndMergeAttributes,
          duplicatedLoopAndMergeAttributes,
        );
        return newBeforeLabel;
      },
      afterLabel: (beforeLabel) => {
        const newAfterLabel = replaceLoopAndMergeAnswerRecalls(
          beforeLabel,
          loopAndMergeAttributes,
          duplicatedLoopAndMergeAttributes,
        );
        return newAfterLabel;
      },
      matrixExtraLabels: (matrixExtraLabel) =>
        matrixExtraLabel.map((extraLabel) => ({
          ...extraLabel,
          label: replaceLoopAndMergeAnswerRecalls(
            extraLabel.label,
            loopAndMergeAttributes,
            duplicatedLoopAndMergeAttributes,
          ),
        })),
    };
    transformedQuestion = transformPropsDeep(
      transformedQuestion,
      loopAndMergePropertyReplaceMap,
      {},
    );
  }

  // second pass to update bipolar matrixAnswers link to question
  if (hasMatrix) {
    const originalAnswers = get(questionData, PROPERTIES.PROPERTY_ANSWERS, []);
    const duplicatedAnswers = get(
      transformedQuestion,
      PROPERTIES.PROPERTY_ANSWERS,
      [],
    );

    const originalToDuplicateMap = originalAnswers.reduce(
      (map, answers, index) => ({
        ...map,
        [answers.refId]: duplicatedAnswers[index].refId,
      }),
      {},
    );

    forEach(
      get(transformedQuestion, 'matrix.matrixAnswers', []),
      (duplicatedMatrixAnswer) => {
        // eslint-disable-next-line no-param-reassign
        duplicatedMatrixAnswer.answerRefId =
          originalToDuplicateMap[duplicatedMatrixAnswer.answerRefId];
      },
    );
  }
  // second pass to update bipolar column link to question
  if (hasMatrix) {
    const duplicatedColumns = get(transformedQuestion, 'matrix.columns', []);

    forEach(duplicatedColumns, (duplicatedColumn) => {
      // we don't want to replace the questionRefId on the column if it's coming from a carried forward question
      // also i think we don't use the questionRefId on the column anymore. So the reassignment below might be wrong.
      if (duplicatedColumn.isCarriedForward) {
        return;
      }
      // eslint-disable-next-line no-param-reassign
      duplicatedColumn.questionRefId = transformedQuestion.refId;
    });
  }

  // third pass to update answerColumnValueTypes to question
  if (hasMatrix) {
    const duplicatedAnswerColumnValueTypes = get(
      transformedQuestion,
      'matrix.answerColumnValueTypes',
      [],
    );

    const originalAnswers = get(questionData, 'answers', []);
    const duplicatedAnswers = get(transformedQuestion, 'answers', []);

    const originalToDuplicateMap = originalAnswers.reduce(
      (map, answers, index) => ({
        ...map,
        [answers.refId]: duplicatedAnswers[index].refId,
      }),
      {},
    );

    const originalColumns = get(questionData, 'matrix.columns', []);
    const duplicatedColumns = get(transformedQuestion, 'matrix.columns', []);

    const originalToDuplicateColumnMap = originalColumns.reduce(
      (map, column, index) => ({
        ...map,
        [column.refId]: duplicatedColumns[index].refId,
      }),
      {},
    );

    forEach(duplicatedAnswerColumnValueTypes, (answerColumnValueType) => {
      // eslint-disable-next-line no-param-reassign
      answerColumnValueType.answerRefId =
        originalToDuplicateMap[answerColumnValueType.answerRefId];

      // eslint-disable-next-line no-param-reassign
      answerColumnValueType.columnRefId =
        originalToDuplicateColumnMap[answerColumnValueType.columnRefId];
    });
  }

  // do not duplicate answers for question types that are handled on the backend
  if (!questionSupportsFeature(transformedQuestion.type, FEATURE_ANSWERS)) {
    delete transformedQuestion.answers;
  }

  if (!questionSupportsFeature(transformedQuestion.type, FEATURE_MATRIX)) {
    delete transformedQuestion.scorepoints;
  }

  if (isDrillDown) {
    const { answers, matrix } = transformedQuestion;
    const { levels } = matrix.validation.drillDownConfigurationDto.config;
    const updatedLevels = getLevelsWithAnswerRefId(answers)(
      getOptionsArrayFromLevels(levels),
    );

    transformedQuestion.matrix.validation.drillDownConfigurationDto.config.levels =
      updatedLevels;
  }

  return transformedQuestion;
};

function replaceVanWestendorpAnswerRecalls(
  text,
  originalVanWestendorp,
  duplicatedVanWestendorp,
) {
  const { refId: originalRefId, answers: originalAnswers } =
    originalVanWestendorp;
  const { refId: duplicatedRefId, answers: duplicatedAnswers } =
    duplicatedVanWestendorp;
  let newPrompt = text;

  originalAnswers.forEach((answer, index) => {
    newPrompt = replaceMentionsRefIdWithOtherInText(
      newPrompt,
      originalRefId,
      duplicatedRefId,
      answer.refId,
      duplicatedAnswers[index].refId,
    );
  });
  return newPrompt;
}

function replaceLoopAndMergeAnswerRecalls(
  text,
  originalLoopAndMergeAttributes,
  duplicatedLoopAndMergeAttributes,
) {
  const loopAndMergeAttributesRefIds = originalLoopAndMergeAttributes.map(
    ({ refId }) => refId,
  );
  const duplicatedLoopAndMergeAttributesRefIds =
    duplicatedLoopAndMergeAttributes.map(({ refId }) => refId);

  let newPrompt = text;
  newPrompt = replaceMentionsAttrRefIdWithOtherInText(
    newPrompt,
    loopAndMergeAttributesRefIds,
    duplicatedLoopAndMergeAttributesRefIds,
  );

  return newPrompt;
}

export function questionHasAnswersInPredefinedTarget(question = {}) {
  switch (question.type) {
    case QUESTION_SINGLE:
    case QUESTION_MULTIPLE:
    case QUESTION_RANKING: {
      const answers = get(question, PROPERTIES.PROPERTY_ANSWERS, []);
      return answers.length > 0;
    }
    case QUESTION_SLIDER: {
      return !hasOnlyDefaultTicks(question);
    }
    case QUESTION_MATRIX_SINGLE:
    case QUESTION_MATRIX_MULTIPLE: {
      return !hasOnlyDefaultScorepoints(question);
    }
    default:
      return false;
  }
}

export function mergePreviousNodesPerNode(
  state,
  logicKeyUpdated,
  previousNodesPerNodeUpdated,
) {
  return previousNodesPerNodeUpdated;
}

export function getSurveyConfigForValidate(state) {
  return {
    completes: state?.survey?.remainingCompletes,
    isUnderEdit: state?.survey?.isUnderEdit,
    isDraft: state?.survey?.isDraft,
    maxResponsesDistributionLink: state?.survey?.maxResponsesDistributionLink,
    isDistributionLink: state?.surveyDetailsExtended?.isDistributionLink,
    isPeeq: state?.surveyDetailsExtended?.isPeeq,
  };
}

// Default used when a distribution-link survey has no maxResponses set yet
// (mirrors the fallback in the UPDATE_LINK_SETTINGS reducer handler).
export const DEFAULT_DLINK_COMPLETES = 20000;

// Derives { completes, remainingCompletes } for the QuestionnairePage `survey` slice.
// For distribution-link surveys the source of truth is `maxResponses` (from the link
// settings endpoint), not the survey-API `completes`. For all other surveys the survey-API
// `completes` is returned unchanged. remainingCompletes is what the quota validators
// ultimately check against (via getSurveyConfigForValidate).
export function deriveSurveyCompletes({
  isDistributionLink,
  maxResponses,
  surveyCompletes,
  completed = 0,
}) {
  const completes = isDistributionLink
    ? maxResponses || DEFAULT_DLINK_COMPLETES
    : surveyCompletes;

  return { completes, remainingCompletes: completes - completed };
}

export const extractSubstateForQuestionnaire = (state) =>
  QUESTIONNAIRE_SUBSTATE_PATHS.reduce((acc, path) => {
    const adjustedPath = path.indexOf('.') >= 0 ? path.split('.')[1] : path;
    acc[adjustedPath] = get(state, path);
    return acc;
  }, {});

export const defaultIdentityTrue = () => true;
export const defaultIdentityUndefined = () => undefined;

export const getAvailableRuleContextTypes = ({
  isPeeq,
  isDistributionLink,
}) => {
  switch (true) {
    case isPeeq:
      return [
        QuestionnaireConditionContext.Answer,
        QuestionnaireConditionContext.Visibility,
        QuestionnaireConditionContext.Concept,
        QuestionnaireConditionContext.Variable,
        QuestionnaireConditionContext.Taxonomy,
      ];
    case isDistributionLink:
      return [
        QuestionnaireConditionContext.Answer,
        QuestionnaireConditionContext.Visibility,
        QuestionnaireConditionContext.Concept,
        QuestionnaireConditionContext.Variable,
      ];
    default:
      return [
        QuestionnaireConditionContext.Answer,
        QuestionnaireConditionContext.Visibility,
        QuestionnaireConditionContext.Concept,
        QuestionnaireConditionContext.Taxonomy,
      ];
  }
};

function hasMention(item = {}) {
  return item.hasPiping;
}

function hasNodeMention(node, predicateFn = hasMention) {
  return predicateFn(node) || hasNodeMentionsOnAnswers(node, predicateFn);
}

export function hasNodeMentions(node, predicateFn = hasMention) {
  if (!node) {
    return false;
  }

  // groupNode
  if (node.questions) {
    return node.questions.some((subNode) =>
      hasNodeMention(subNode, predicateFn),
    );
  }

  return hasNodeMention(node, predicateFn);
}

export function hasNodesMentions(nodes, predicateFn = hasMention) {
  return nodes.some((node) => hasNodeMentions(node, predicateFn));
}

export function hasNodeMentionsOnAnswers(node = {}, predicateFn = hasMention) {
  if (isGroup(node.type)) {
    return false;
  }
  const hasAnswerMentions = Boolean(node.answers?.some(predicateFn));
  const hasScorepointMentions = Boolean(node.scorepoints?.some(predicateFn));
  const hasNewMatrixColumnMentions = Boolean(
    node.matrix?.columns?.some(predicateFn),
  );
  const hasMatrixAnswersMentions = Boolean(
    node.matrix?.matrixAnswers?.some(predicateFn),
  );
  const hasMatrixExtraLabelsMentions = Boolean(
    node.matrix?.matrixExtraLabels?.some(predicateFn),
  );

  return (
    hasAnswerMentions ||
    hasScorepointMentions ||
    hasNewMatrixColumnMentions ||
    hasMatrixAnswersMentions ||
    hasMatrixExtraLabelsMentions
  );
}

export function getMentionsOnNodesInsideGroup(
  groupNode,
  previousNodesPerNode,
  { nodes },
) {
  if (!groupNode || isEmpty(groupNode.questions)) {
    return [];
  }

  const childNodes = groupNode.questions;
  const childNodesRefIds = childNodes.map((childNode) => childNode.refId);

  return childNodes.reduce((accumulator, childNode) => {
    const getMentionsPerNode = getNodeMentions(childNode);
    const previousChildNodesRefIds = intersection(
      childNodesRefIds,
      previousNodesPerNode[childNode.refId],
    );

    return previousChildNodesRefIds.reduce(
      (acc, refId) => [
        ...acc,
        ...getMentionsPerNode({
          refId,
          prompts: nodes[refId].prompts,
        }),
      ],
      accumulator,
    );
  }, []);
}

export function questionHasDiff({ questionsDiff, question }) {
  if (!questionsDiff || !questionsDiff.changes || !question) return false;
  return questionsDiff.changes.some((change) =>
    change.path.includes(question.refId),
  );
}

export function enrichNodeForMentions(node, index, isTranslation) {
  const propertiesToPick = isTranslation
    ? translationPropertiesForMentions
    : nodePropertiesForMentions;
  const updatedNode = {
    ...pick(node, propertiesToPick),
    index,
  };

  if (isLoopAndMergeGroup(node.type)) {
    updatedNode.attributes = node.attributes.map(
      (attribute, attributeIndex) => {
        const name =
          attribute.name ||
          attribute.placeholder ||
          `Variable ${attributeIndex + 1}`;

        return {
          ...attribute,
          name,
          label: name,
          prompt: '',
          [PROPERTIES.PROPERTY_LOOP_MERGE]: true,
          values: undefined,
        };
      },
    );
  }

  return updatedNode;
}

export function prepareLoopAndMergeFromQuestionConfigurationForStore(
  loopAndMergeFromQuestionConfiguration,
) {
  if (isEmpty(loopAndMergeFromQuestionConfiguration)) {
    return loopAndMergeFromQuestionConfiguration;
  }

  const newLoopAndMergeFromQuestionConfiguration = {
    ...loopAndMergeFromQuestionConfiguration,
  };
  if (newLoopAndMergeFromQuestionConfiguration.profilingQuestionId) {
    newLoopAndMergeFromQuestionConfiguration.isProfiling = true;
    newLoopAndMergeFromQuestionConfiguration.questionRefId =
      newLoopAndMergeFromQuestionConfiguration.profilingQuestionId;
  } else {
    newLoopAndMergeFromQuestionConfiguration.questionRefId =
      newLoopAndMergeFromQuestionConfiguration.mergeQuestionRefId;
  }

  return {
    ...omit(newLoopAndMergeFromQuestionConfiguration, [
      'mergeQuestionRefId',
      'profilingQuestionId',
    ]),
  };
}

export function getEntityRefId(entity) {
  return entity.refId;
}

export function getEntityPropertyByKey(propertyKey) {
  return (entity) => entity[propertyKey];
}

const PipedAttribute = (
  text = '',
  maxLength = MAX_PIPED_QUESTION_LENGTH,
  key,
) => (
  <span className="piped-attribute-wrapper">
    <span key={key} className="piped-attribute">
      <Pficon className="position-relative" type="loop-merge" size={10} />{' '}
      {text.length > maxLength ? `${text.substring(0, maxLength)}...` : text}
    </span>
  </span>
);
const formatter = (text, key) => (
  <span key={key} className="piped-question-normal-text margin-right-xs">
    {stripHTMLTags(text)}
  </span>
);

export function getTransformedPromptForVariables({
  questions = [],
  question,
  attributes,
  index,
}) {
  const { prompt } = question;
  let updatedBody = String(prompt || '');
  let stringIndexStart = 0;
  const formattedParts = [];
  const promptParts = [];
  let match;
  do {
    match = new RegExp(variableRegex).exec(updatedBody);
    if (match) {
      const [partToReplace, attrRefId] = match;
      const targetAttribute = find(attributes, { refId: attrRefId });

      if (!targetAttribute) {
        return {};
      }
      let attributeName = get(targetAttribute.values, `[${index}].text`, '');

      const cfMatch = new RegExp(pipingRegExAllWithoutVariables).exec(
        attributeName,
      );
      if (cfMatch) {
        // this implies that it's a recall of a previous question
        const targetQuestion = find(
          questions,
          (q) =>
            q.originalRefId === cfMatch[PIPE_EXPRESSION_MATCHING.REF_ID] ||
            q.refId === cfMatch[PIPE_EXPRESSION_MATCHING.REF_ID],
        );

        if (targetQuestion) {
          attributeName = targetQuestion.prompt || '';
        }
      }

      const currentTextPart = updatedBody.substring(
        stringIndexStart,
        match.index,
      );

      promptParts.push(currentTextPart);
      formattedParts.push(formatter(currentTextPart));

      promptParts.push(stripHTMLTags(attributeName));
      formattedParts.push(PipedAttribute(stripHTMLTags(attributeName)));

      stringIndexStart = match.index;
      updatedBody = updatedBody.substring(
        stringIndexStart + partToReplace.length,
      );
    }
  } while (match);

  if (updatedBody) {
    promptParts.push(updatedBody);
    formattedParts.push(formatter(updatedBody));
  }

  return { promptParts: promptParts.join(''), formattedParts };
}

export const getRemoveOrRestoreTooltipMessage = (
  action,
  valueType,
  nodeType,
) => {
  const { tooltip, tooltipValues } = getToolTip(action, nodeType);

  if (valueType !== ActionVisibilityValueType.Node) {
    tooltipValues.entityName = messages[valueType].defaultMessage;
  }

  return {
    tooltip,
    tooltipValues,
  };
};

export const withLogicTitleMessage = (type) => {
  switch (type) {
    case OPTION_TYPES.ANSWER:
      return messages.confirmModal.answer.title;
    case OPTION_TYPES.ROW:
      return messages.confirmModal.statement.title;
    case OPTION_TYPES.COLUMN:
      return messages.confirmModal.scalepoint.title;
    case OPTION_TYPES.NODE:
      return messages.confirmModal.node.title;
    default:
      return messages.confirmModal.answer.title;
  }
};

export const withLogicCarryForwardTitleMessage = (type) => {
  switch (type) {
    case OPTION_TYPES.ANSWER:
      return messages.confirmModal.answer.titleCarryForward;
    case OPTION_TYPES.ROW:
      return messages.confirmModal.statement.titleCarryForward;
    case OPTION_TYPES.COLUMN:
      return messages.confirmModal.scalepoint.titleCarryForward;
    case OPTION_TYPES.NODE:
      return messages.confirmModal.node.titleCarryForward;
    default:
      return messages.confirmModal.answer.titleCarryForward;
  }
};

export const withLogicDescriptionMessage = (type) => {
  switch (type) {
    case OPTION_TYPES.ANSWER:
      return messages.confirmModal.answer.body;
    case OPTION_TYPES.ROW:
      return messages.confirmModal.statement.body;
    case OPTION_TYPES.COLUMN:
      return messages.confirmModal.scalepoint.body;
    case OPTION_TYPES.NODE:
      return messages.confirmModal.node.bodyBefore;
    default:
      return messages.confirmModal.answer.body;
  }
};

/**
 * Answer-quota targets pointing at an unconditionally hidden option, and whether
 * removing them empties the question.
 */
function findOrphanedQuotaTargets({
  ql,
  questionRefId,
  unconditionallyHiddenItems,
}) {
  const none = { orphanedIds: [], emptiesTheQuestion: false };
  if (unconditionallyHiddenItems.length === 0) return none;

  let statements;
  try {
    statements = questionnaireLogicService.findNodeStatementsByNodeId(
      ql,
      questionRefId,
    )?.statements;
  } catch (error) {
    // Unknown node in QL — skip rather than fail the pass.
    return none;
  }
  if (!statements?.length) return none;

  // Simple answer quotas only; conditional quotas share the same option refId.
  const simpleQuotaStatements = getFilteredQuotaStatementsByType(
    statements,
    QuotaConditionTypeEnum.Simple,
  );

  const orphanedIds = simpleQuotaStatements
    .filter(({ rule }) => {
      const value = rule?.conditions?.[0]?.value;
      if (!value) return false;

      // Match the option's own refId, not a carry-forward source.
      return typeof value === 'object'
        ? unconditionallyHiddenItems.includes(value.row) ||
            unconditionallyHiddenItems.includes(value.column)
        : unconditionallyHiddenItems.includes(value);
    })
    .map(({ id }) => id);

  return {
    orphanedIds,
    emptiesTheQuestion:
      orphanedIds.length > 0 &&
      orphanedIds.length === simpleQuotaStatements.length,
  };
}

function applyOrphanedQuotaTargetRemoval({ state, orphanedTargetsPerNode }) {
  if (orphanedTargetsPerNode.length === 0) return state;

  const newQl = orphanedTargetsPerNode.reduce((ql, entry) => {
    const { questionRefId, orphanedIds } = entry;
    const removed = new Set(orphanedIds);
    const statements =
      questionnaireLogicService.findNodeStatementsByNodeId(ql, questionRefId)
        ?.statements || [];

    return questionnaireLogicService.swapNodeStatements(
      ql,
      questionRefId,
      new NodeStatements({
        nodeId: questionRefId,
        statements: statements.filter(({ id }) => !removed.has(id)),
      }),
    );
  }, state.questionnaireLogic.ql);

  return Object.assign({}, state, {
    questionnaireLogic: updateState({
      ql: newQl,
      questionnaireLogic: state.questionnaireLogic,
      shouldUpdateVersion: false,
    }),
  });
}

export function validateQuotaAndUpdateState(state, specificNodeRefId = null) {
  const { errors: globalErrors } = state;
  const nodes = get(state, 'surveyDetailsExtended.questions', []);
  const surveyStatusName = get(state, 'surveyDetailsExtended.statusName', '');
  const uncoditionallyHiddenItems = getUnconditionallyHiddenItems(state);
  const questionsIdsToRemoveSimpleQuotas = [];
  const questionsIdsToRemoveAdvancedQuotas = [];
  const orphanedTargetsPerNode = [];
  const quotasMinimalMap = makeSelectQuestionQuotasMinimalMap({
    questionnairePage: state,
  });

  const { updatedQuestions, questionnaireErrors } = attachNodesQuotaErrors({
    nodes,
    specificNodeRefId,
    quotasMinimalMap,
    globalErrors,
    questionsIdsToRemoveSimpleQuotas,
    questionsIdsToRemoveAdvancedQuotas,
    orphanedTargetsPerNode,
    ql: get(state, 'questionnaireLogic.ql'),
    uncoditionallyHiddenItems,
    surveyConfig: getSurveyConfigForValidate(state),
    surveyStatusName,
  });

  // Remove orphans before all-or-nothing quota removal, which re-enters this pass.
  let cleanedUpState = applyOrphanedQuotaTargetRemoval({
    state,
    orphanedTargetsPerNode,
  });
  if (questionsIdsToRemoveSimpleQuotas.length > 0) {
    questionsIdsToRemoveSimpleQuotas.forEach((questionRefId) => {
      cleanedUpState = removeNodeQuotaRulesActionHandler(cleanedUpState, {
        questionRefId,
        matcher: QuotaConditionTypeEnum.Simple,
      });
    });
  }
  if (questionsIdsToRemoveAdvancedQuotas.length > 0) {
    questionsIdsToRemoveAdvancedQuotas.forEach((questionRefId) => {
      cleanedUpState = removeNodeQuotaRulesActionHandler(cleanedUpState, {
        questionRefId,
        matcher: QuotaConditionTypeEnum.Advanced,
      });
    });
  }

  // Strip quotaLimitType from nodes that no longer have simple quota rules.
  const nodesLosingSimpleQuotas = new Set([
    ...questionsIdsToRemoveSimpleQuotas,
    ...orphanedTargetsPerNode
      .filter(({ emptiesTheQuestion }) => emptiesTheQuestion)
      .map(({ questionRefId }) => questionRefId),
  ]);
  const nodeKeepsQuotaLimitType = (refId) =>
    Boolean(quotasMinimalMap[refId]?.simpleQuotasPresent) &&
    !nodesLosingSimpleQuotas.has(refId);
  const enforceQuotaLimitTypeInvariant = (questions) =>
    questions.map((question) => {
      let nextQuestion = question;
      if (
        question.quotaLimitType !== undefined &&
        !nodeKeepsQuotaLimitType(question.refId)
      ) {
        const { quotaLimitType, ...rest } = question;
        nextQuestion = rest;
      }
      if (nextQuestion.questions) {
        return {
          ...nextQuestion,
          questions: enforceQuotaLimitTypeInvariant(nextQuestion.questions),
        };
      }
      return nextQuestion;
    });
  const finalQuestions = enforceQuotaLimitTypeInvariant(updatedQuestions);

  return Object.assign({}, cleanedUpState, {
    surveyDetailsExtended: {
      ...cleanedUpState.surveyDetailsExtended,
      questions: finalQuestions,
    },
    errors: questionnaireErrors,
  });
}

function canNodeHaveAdvancedQuotas({ parentNode = null }) {
  if (parentNode) {
    const groupAllowsQuotasInside = GROUP_TYPES_ALLOW_ADVANCED_QUOTAS.includes(
      parentNode.type,
    );
    if (!groupAllowsQuotasInside) return false;
  }

  return true;
}

/**
 * Unconditionally hidden option refIds from the Hide-outcome list.
 */
export function getUnconditionallyHiddenItems(
  state,
  logicKey = 'questionnaireLogic',
) {
  return get(state, [logicKey, 'answersWithLogicFromOutcomesHide'], []);
}

/**
 * Quota-eligible answers/rows/columns with unconditionally hidden options removed.
 * `unconditionallyHiddenItems` is the Hide-outcome list.
 * `isEligibleItem({ answerRefId, columnRefId })`, when given, also drops
 * ineligible answers, and matrix rows/columns without any eligible cell. It is
 * returned alongside, so the quota spread can skip ineligible matrix cells.
 */
export function getQuotaEligibleOptions({
  node = {},
  unconditionallyHiddenItems = [],
  isEligibleItem,
}) {
  const isVisible = ({ refId }) => !unconditionallyHiddenItems.includes(refId);
  // `|| []` also covers null; lodash get's default only covers undefined.
  const visible = (path) => (get(node, path) || []).filter(isVisible);

  const matrixOptions = (columnsKey, columnsPath) => {
    const answers = visible('answers');
    const columns = visible(columnsPath);
    if (!isEligibleItem) return { answers, [columnsKey]: columns };

    const isEligibleCell = (answer, column) =>
      isEligibleItem({ answerRefId: answer.refId, columnRefId: column.refId });
    return {
      answers: answers.filter((answer) =>
        columns.some((column) => isEligibleCell(answer, column)),
      ),
      [columnsKey]: columns.filter((column) =>
        answers.some((answer) => isEligibleCell(answer, column)),
      ),
      isEligibleItem,
    };
  };

  switch (node.type) {
    case QUESTION_SINGLE:
    case QUESTION_MULTIPLE:
      if (!isEligibleItem) return { answers: visible('answers') };
      return {
        answers: visible('answers').filter(({ refId }) =>
          isEligibleItem({ answerRefId: refId }),
        ),
        isEligibleItem,
      };
    case QUESTION_MATRIX_SINGLE:
    case QUESTION_MATRIX_MULTIPLE:
      return matrixOptions('scorepoints', 'scorepoints');
    case QUESTION_MATRIX_BIPOLAR:
      return matrixOptions('columns', 'matrix.columns');
    default:
      return {};
  }
}

/**
 * The quota eligibility predicate for a node: on screening nodes, only the
 * visible items whose screener type can carry a quota; undefined otherwise
 * (every item is eligible).
 */
export function getQuotaEligibilityForNode({
  node,
  ql,
  unconditionallyHiddenItems = [],
}) {
  if (!node?.isScreening) return undefined;

  return makeScreeningQuotaEligibility({
    node,
    ql,
    unconditionallyHiddenItems,
  });
}

/**
 * Whether the question has enough visible options to hold per-answer quotas.
 * `isEligibleItem`, when given, only counts the options it accepts.
 */
export function hasEnoughVisibleQuotaOptions({
  node = {},
  unconditionallyHiddenItems = [],
  isEligibleItem,
}) {
  const {
    answers = [],
    scorepoints = [],
    columns = [],
  } = getQuotaEligibleOptions({
    node,
    unconditionallyHiddenItems,
    isEligibleItem,
  });

  switch (node.type) {
    case QUESTION_SINGLE:
    case QUESTION_MULTIPLE:
      return answers.length > 1;
    case QUESTION_MATRIX_SINGLE:
    case QUESTION_MATRIX_MULTIPLE:
      // Excludes the "none of these" column; target generation still includes it.
      return answers.length > 0 && filterVisibleColumns(scorepoints).length > 1;
    case QUESTION_MATRIX_BIPOLAR:
      return answers.length > 0 && columns.length > 1;
    default:
      return false;
  }
}

/**
 * Whether the question can hold per-answer quotas: a type and parent group
 * that allow them, and at least two options that can carry one (on a
 * screening question, allowed and visible, read from `ql`).
 */
export function canNodeHaveSimpleQuotas({
  node = {},
  ql,
  unconditionallyHiddenItems = [],
  parentNode = null,
}) {
  const typeSupportsQuotas = NODE_TYPES_SUPPORT_SIMPLE_QUOTAS.includes(
    node.type,
  );
  if (!typeSupportsQuotas) return false;

  if (parentNode) {
    const groupAllowsQuotasInside = GROUP_TYPES_ALLOW_SIMPLE_QUOTAS.includes(
      parentNode.type,
    );
    if (!groupAllowsQuotasInside) return false;
  }

  return hasEnoughVisibleQuotaOptions({
    node,
    unconditionallyHiddenItems,
    isEligibleItem: getQuotaEligibilityForNode({
      node,
      ql,
      unconditionallyHiddenItems,
    }),
  });
}

/**
 * Whether a change would drop the question below the quota-eligibility
 * threshold, turning its quotas off. `before` and `after` are the
 * `{ ql, unconditionallyHiddenItems }` around the change: hiding an option
 * adds it to the hidden items, a screener-type change rewrites the rules.
 * False if the question is already ineligible. Pass the immediate parent
 * group as parentNode.
 */
export function wouldChangeRemoveQuotaEligibility({
  node,
  parentNode = null,
  before,
  after,
}) {
  const isEligibleNow = canNodeHaveSimpleQuotas({
    node,
    parentNode,
    ...before,
  });
  if (!isEligibleNow) return false;

  return !canNodeHaveSimpleQuotas({ node, parentNode, ...after });
}

const QUOTA_ERROR_KEYS = [
  'quotaMissing',
  'minimumResponsesMissing',
  'quotaSumIsNot100',
  'minimumQuotaSumExceedsCompletes',
  'hasQuotaLessThanCollected',
  'quotasFulfilled',
];

const SCREENING_ANSWER_ERROR_KEYS = [
  'missingScreeningAnswers',
  'missingAllowedAnswer',
  'missingNotAllowedAnswer',
];

function attachNodesQuotaErrors({
  nodes,
  specificNodeRefId, // use this for early return
  quotasMinimalMap,
  globalErrors,
  questionsIdsToRemoveSimpleQuotas,
  questionsIdsToRemoveAdvancedQuotas,
  orphanedTargetsPerNode,
  ql,
  uncoditionallyHiddenItems,
  surveyConfig,
  parentNode = null, // for recursive use only
  surveyStatusName,
}) {
  let questionnaireErrors = globalErrors;

  const updatedQuestions = nodes.map((question) => {
    const hasSimpleQuotas = Boolean(
      quotasMinimalMap[question.refId]?.simpleQuotasPresent,
    );
    const hasAdvancedQuotas = Boolean(
      quotasMinimalMap[question.refId]?.advancedQuotasPresent,
    );
    const updatedQuestion = { ...question };

    // [TODO:gabe] we should get rid of this canHaveQuotas variable and handle this logic based
    // on the question type and features it supports instead of hardcoding exceptions
    const nodeCanHaveSimpleQuotas = canNodeHaveSimpleQuotas({
      node: updatedQuestion,
      ql,
      parentNode,
      unconditionallyHiddenItems: uncoditionallyHiddenItems,
    });
    const nodeCanHaveAdvancedQuotas = canNodeHaveAdvancedQuotas({
      parentNode,
    });

    let emptiedByOrphanCleanup = false;

    if (hasSimpleQuotas && !nodeCanHaveSimpleQuotas) {
      questionsIdsToRemoveSimpleQuotas.push(updatedQuestion.refId);
    } else if (hasSimpleQuotas) {
      // Skip orphan cleanup when the question is already losing all its quotas.
      const { orphanedIds, emptiesTheQuestion } = findOrphanedQuotaTargets({
        ql,
        questionRefId: updatedQuestion.refId,
        unconditionallyHiddenItems: uncoditionallyHiddenItems,
      });
      if (orphanedIds.length > 0) {
        orphanedTargetsPerNode.push({
          questionRefId: updatedQuestion.refId,
          orphanedIds,
          emptiesTheQuestion,
        });
        emptiedByOrphanCleanup = emptiesTheQuestion;
      }
    }
    if (hasAdvancedQuotas && !nodeCanHaveAdvancedQuotas) {
      questionsIdsToRemoveAdvancedQuotas.push(updatedQuestion.refId);
    }

    if (updatedQuestion.questions) {
      const {
        updatedQuestions: updatedSubQuestions,
        questionnaireErrors: updatedQuestionnaireErrors,
      } = attachNodesQuotaErrors({
        nodes: updatedQuestion.questions,
        specificNodeRefId,
        quotasMinimalMap,
        globalErrors,
        questionsIdsToRemoveSimpleQuotas,
        questionsIdsToRemoveAdvancedQuotas,
        orphanedTargetsPerNode,
        ql,
        uncoditionallyHiddenItems,
        surveyConfig,
        parentNode: updatedQuestion,
        surveyStatusName,
      });
      questionnaireErrors = updatedQuestionnaireErrors;
      updatedQuestion.questions = updatedSubQuestions;
    }

    let nodeQuotaErrors = {};
    // Skip validation for questions whose quotas this pass is about to remove.
    if (hasSimpleQuotas && nodeCanHaveSimpleQuotas && !emptiedByOrphanCleanup) {
      nodeQuotaErrors = getNodeQuotaErrors(
        updatedQuestion,
        quotasMinimalMap[updatedQuestion.refId],
        uncoditionallyHiddenItems,
        surveyConfig,
        surveyStatusName,
        getQuotaEligibilityForNode({
          node: updatedQuestion,
          ql,
          unconditionallyHiddenItems: uncoditionallyHiddenItems,
        }),
      );
    }

    // Screening answer errors read the screening rules, so they come from
    // this pass, which runs once the rules are up to date.
    const screeningErrors = updatedQuestion.isScreening
      ? getNodeScreeningErrors(
          updatedQuestion,
          decodeScreenerTypes({
            node: updatedQuestion,
            statements: getNodeStatements(ql, updatedQuestion.refId),
          }),
        )
      : {};

    const updatedNodeErrors = {
      ...omit(updatedQuestion.errors, [
        ...QUOTA_ERROR_KEYS,
        ...SCREENING_ANSWER_ERROR_KEYS,
      ]),
      ...screeningErrors,
      ...nodeQuotaErrors,
    };

    questionnaireErrors = updateQuestionnaireErrors({
      globalErrors: questionnaireErrors,
      nodeRefId: updatedQuestion.refId,
      groupRefId: updatedQuestion.groupRefId,
      nodeErrors: updatedNodeErrors,
      nodeType: updatedQuestion.type,
    });

    return { ...updatedQuestion, errors: updatedNodeErrors };
  });

  return { updatedQuestions, questionnaireErrors };
}

const getNodeScreeningErrors = (node, screenerTypes) => {
  switch (node.type) {
    case QUESTION_SINGLE:
    case QUESTION_MULTIPLE:
      return singleMultipleScreeningValidator({ node, screenerTypes });
    default:
      return {};
  }
};

const getNodeQuotaErrors = (
  node,
  nodeQuota,
  uncoditionallyHiddenItems,
  surveyConfig,
  surveyStatusName,
  isEligibleItem,
) => {
  switch (node.type) {
    case QUESTION_SINGLE:
    case QUESTION_MULTIPLE:
      return singleMultipleQuotaValidator(
        node,
        nodeQuota,
        uncoditionallyHiddenItems,
        surveyConfig,
        surveyStatusName,
        isEligibleItem,
      );
    case QUESTION_MATRIX_SINGLE:
    case QUESTION_MATRIX_MULTIPLE:
      return matrixQuotaValidator(
        node,
        nodeQuota,
        uncoditionallyHiddenItems,
        surveyConfig,
        surveyStatusName,
      );
    case QUESTION_MATRIX_BIPOLAR:
      return bipolarQuotaValidator(
        node,
        nodeQuota,
        uncoditionallyHiddenItems,
        surveyConfig,
        surveyStatusName,
      );
    default:
      return null;
  }
};

export const hasMissingMaxResponsesForDlinkQuotas = ({
  isDistributionLink,
  maxResponses,
}) =>
  isDistributionLink &&
  (isUndefined(maxResponses) || maxResponses === null || maxResponses < 0);

// Until link settings have been fetched, maxResponses is indistinguishable
// from "no limit set"; treating it as missing would let QuotaAnswersSetting's
// auto-disable effect strip quota rules while the settings request is still
// in flight (RES-12057 race).
export const hasConfirmedMissingMaxResponsesForDlinkQuotas = ({
  isDistributionLink,
  maxResponses,
  linkSettingsFetched,
}) =>
  Boolean(linkSettingsFetched) &&
  hasMissingMaxResponsesForDlinkQuotas({ isDistributionLink, maxResponses });

export const hasAnswerLimitsEnabled = (question = {}) =>
  Boolean(question.min || question.max);

// Committed mode from persisted fields, ignoring in-flight pendingQuotaMode.
export const hasQuestionQuotasSimple = ({ refId, quotasMinimalMap }) => {
  const entry = quotasMinimalMap?.[refId];
  return (
    deriveQuotaMode({
      simpleQuotasPresent: entry?.simpleQuotasPresent,
      quotaLimitType: entry?.quotaLimitType,
    }) === QUOTA_MODE.MAX
  );
};

export const hasQuestionMinimumResponses = ({ refId, quotasMinimalMap }) => {
  const entry = quotasMinimalMap?.[refId];
  return (
    deriveQuotaMode({
      simpleQuotasPresent: entry?.simpleQuotasPresent,
      quotaLimitType: entry?.quotaLimitType,
    }) === QUOTA_MODE.MIN
  );
};

export const hasQuestionQuotasAdvanced = ({ refId, quotasMinimalMap }) =>
  Boolean(quotasMinimalMap?.[refId]?.advancedQuotasPresent);

/**
 * Live answer quota mode (`none` | `max` | `min`): pending while a mode change
 * is in flight, otherwise derived from persisted state. Missing entry is `none`.
 */
export const getQuestionQuotaMode = ({ refId, quotasMinimalMap }) =>
  quotasMinimalMap?.[refId]?.quotaMode ?? QUOTA_MODE.NONE;

/**
 * Why an answer-quota switch is disabled. Later assignments win. `null` means interactive.
 */
export const QUOTA_SETTING_DISABLED_REASON = {
  NOT_ENOUGH_VISIBLE_OPTIONS: 'notEnoughVisibleOptions',
  VISIBILITY: 'visibility',
  CARRY_FORWARD: 'carryForward',
  ANSWER_LIMITS: 'answerLimits',
};

/**
 * Per-mode `{ canEnter, disabledReason }` for the answer-quota switches.
 * `canEnter` forces an already-on mode off; `disabledReason` greys the switch
 * and supplies its tooltip. `hasEnoughVisibleOptions` feeds disabledReason only.
 */
export const getQuotaModeAvailability = ({
  isDistributionLink,
  linkMaxResponses,
  linkSettingsFetched,
  hasDisplayAnswerLogic,
  hasVisibilityAnswerRules,
  hasAdvancedQuotas,
  hasSimpleQuotas,
  hasMinimumResponses,
  hasCarryForwardEntries = false,
  hasCarriedForwardAnswers = false,
  hasAnswerLimits = false,
  hasEnoughVisibleOptions = true,
}) => {
  const sharedBlocked =
    hasConfirmedMissingMaxResponsesForDlinkQuotas({
      isDistributionLink,
      maxResponses: linkMaxResponses,
      linkSettingsFetched,
    }) ||
    hasDisplayAnswerLogic ||
    hasAdvancedQuotas;

  let maxDisabledReason = null;
  // Lowest-priority reason; later assignments override it.
  if (!hasEnoughVisibleOptions) {
    maxDisabledReason =
      QUOTA_SETTING_DISABLED_REASON.NOT_ENOUGH_VISIBLE_OPTIONS;
  }
  if (hasVisibilityAnswerRules) {
    maxDisabledReason = QUOTA_SETTING_DISABLED_REASON.VISIBILITY;
  }
  if (hasCarriedForwardAnswers) {
    maxDisabledReason = QUOTA_SETTING_DISABLED_REASON.CARRY_FORWARD;
  }

  let minDisabledReason = maxDisabledReason;
  if (hasAnswerLimits) {
    minDisabledReason = QUOTA_SETTING_DISABLED_REASON.ANSWER_LIMITS;
  }

  return {
    [QUOTA_MODE.MAX]: {
      canEnter: !(sharedBlocked || hasMinimumResponses),
      disabledReason: maxDisabledReason,
    },
    [QUOTA_MODE.MIN]: {
      canEnter: !(
        sharedBlocked ||
        hasSimpleQuotas ||
        hasCarryForwardEntries ||
        hasAnswerLimits
      ),
      disabledReason: minDisabledReason,
    },
  };
};

export const getQuestionSupportsQuotaAssignment = (questionType) =>
  NODE_TYPES_SUPPORT_QUOTA_ASSIGNMENT.includes(questionType);

export const getShowQuotaAssignedCount = ({
  isDraft,
  isQuotasEnabled,
  isMinimumResponsesEnabled = false,
  questionType,
  quotaAssignmentStrategy,
}) =>
  !isDraft &&
  isQuotasEnabled &&
  !isMinimumResponsesEnabled &&
  getQuestionSupportsQuotaAssignment(questionType) &&
  isSingleAssignmentStrategy(quotaAssignmentStrategy);

/**
 * Quota-assignment strategy to persist, based on question type.
 */
export const resolveQuotaAssignmentStrategyForQuestion = ({
  quotaAssignmentStrategy,
  isQuotasEnabled,
  questionType,
}) =>
  resolveQuotaAssignmentStrategyForPersist({
    quotaAssignmentStrategy,
    isQuotasEnabled,
    supportsQuotaFillMenu: getQuestionSupportsQuotaAssignment(questionType),
  });

/**
 * Simple-quota and minimum-responses enabled state, plus whether max mode
 * changed between renders.
 */
export const getQuotaToggleTransition = (
  prevQuotasMinimalMap,
  quotasMinimalMap,
  questionRefId,
) => {
  const isQuotasEnabled = hasQuestionQuotasSimple({
    refId: questionRefId,
    quotasMinimalMap,
  });
  const wasQuotasEnabled = hasQuestionQuotasSimple({
    refId: questionRefId,
    quotasMinimalMap: prevQuotasMinimalMap,
  });
  const isMinimumResponsesEnabled = hasQuestionMinimumResponses({
    refId: questionRefId,
    quotasMinimalMap,
  });

  return {
    isQuotasEnabled,
    isMinimumResponsesEnabled,
    didQuotaToggle: isQuotasEnabled !== wasQuotasEnabled,
  };
};
